import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import AdminUserManagement from './AdminUserManagement';
import BackgroundImageManager from '../components/BackgroundImageManager';
import AdminPropertyUpload from '../components/AdminPropertyUpload';
import { formatPrice } from '../utils/formatters';
import MapComponent from '../components/MapComponent';
import { MapContainer, TileLayer, Rectangle, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { apiUrl, storageUrl, withStorageFallback, normalizeMediaUrl } from '../utils/apiBase';
import { adminAPI } from '../services/api';

const money = (v) => Number(v ?? 0).toFixed(2);

// Component to handle image loading errors gracefully
// Fit map bounds to show all search rectangles (used in Searches tab)
const FitBoundsToSearches = ({ searches }) => {
  const map = useMap();
  const hasBounds = searches?.some((s) => s.bounds && s.bounds.length === 2);
  React.useEffect(() => {
    if (!hasBounds || !map) return;
    const latLngs = [];
    searches.forEach((s) => {
      if (s.bounds && s.bounds.length === 2) {
        latLngs.push(s.bounds[0], s.bounds[1]);
      }
    });
    if (latLngs.length >= 2) {
      try {
        const b = L.latLngBounds(latLngs);
        map.fitBounds(b, { padding: [30, 30], maxZoom: 14 });
      } catch (e) {
        // ignore
      }
    }
  }, [map, hasBounds, searches]);
  return null;
};

const PropertyImageThumbnail = ({ src, alt, className = "w-12 h-12 rounded-lg object-cover", onClick }) => {
  const [imageError, setImageError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [fallbackTried, setFallbackTried] = useState(false);

  useEffect(() => {
    setImageError(false);
    setCurrentSrc(src);
    setFallbackTried(false);
  }, [src]);

  if (imageError) {
    return (
      <div className={`${className} bg-gray-200 flex items-center justify-center text-gray-400 text-xs`}>
        No Img
      </div>
    );
  }

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      onClick={onClick}
      onError={() => {
        if (!fallbackTried) {
          const fallback = withStorageFallback(currentSrc);
          if (fallback && fallback !== currentSrc) {
            setFallbackTried(true);
            setCurrentSrc(fallback);
            return;
          }
        }
        setImageError(true);
      }}
    />
  );
};

const AdminDashboard = () => {
  const { t } = useTranslation();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [maintenanceSettings, setMaintenanceSettings] = useState({ enabled: false, updated_at: null });
  const [maintenanceBusy, setMaintenanceBusy] = useState(false);
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [propertyStatusFilter, setPropertyStatusFilter] = useState('all');
  const [propertyRevisionFilter, setPropertyRevisionFilter] = useState('all');
  const [propertyAgentContactFilter, setPropertyAgentContactFilter] = useState('all');
  const [propertyPage, setPropertyPage] = useState(1);
  const [propertyPagination, setPropertyPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 15,
  });
  const [allPropertiesForMap, setAllPropertiesForMap] = useState([]);
  const [mapMarkersLoading, setMapMarkersLoading] = useState(false);
  const [focusedProperty, setFocusedProperty] = useState(null);
  const [focusedPropertyId, setFocusedPropertyId] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [revenueStats, setRevenueStats] = useState(null);
  const [revenuePreview, setRevenuePreview] = useState(null);
  const [revenuePreviewUsers, setRevenuePreviewUsers] = useState(null);
  const [distributions, setDistributions] = useState([]);
  const [selectedDistributionId, setSelectedDistributionId] = useState(null);
  const [detailUser, setDetailUser] = useState(null);

  // Admin accounts (super admin only)
  const [adminAccounts, setAdminAccounts] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminsError, setAdminsError] = useState(null);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [adminEditForm, setAdminEditForm] = useState({
    name: '',
    email: '',
    role: 'admin',
    is_active: true,
    password: '',
    password_confirmation: '',
  });
  const [detailProps, setDetailProps] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [rateInput, setRateInput] = useState('20');
  const [showMinPayoutModal, setShowMinPayoutModal] = useState(false);
  const [minPayoutInput, setMinPayoutInput] = useState('50');
  const [showManualPayout, setShowManualPayout] = useState(false);
  const [manualPayload, setManualPayload] = useState({ ud: null, paypalEmail: '', subject: '', amount: '', body: '' });
  const [termsSettings, setTermsSettings] = useState({ land_seeker: '', land_reporter: '', updated_at: null });
  const [termsInput, setTermsInput] = useState({ land_seeker: '', land_reporter: '' });
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsSaving, setTermsSaving] = useState(false);
  const [termsError, setTermsError] = useState(null);
  const [invoiceSettings, setInvoiceSettings] = useState({
    company_name: '',
    address_line1: '',
    address_line2: '',
    zip: '',
    city: '',
    state: '',
    country: '',
    tax_id: '',
    email: '',
    updated_at: null,
  });
  const [invoiceInput, setInvoiceInput] = useState({
    company_name: '',
    address_line1: '',
    address_line2: '',
    zip: '',
    city: '',
    state: '',
    country: '',
    tax_id: '',
    email: '',
  });
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [areaSearches, setAreaSearches] = useState({ searches: [], top_areas: [] });
  const [areaSearchesLoading, setAreaSearchesLoading] = useState(false);
  const [areaSearchesError, setAreaSearchesError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });
  const [monthlyTransactions, setMonthlyTransactions] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState(null);
  const [planInputs, setPlanInputs] = useState({});
  const [savingPlanId, setSavingPlanId] = useState(null);
  const [verificationLinks, setVerificationLinks] = useState({});
  const [verificationLoadingIds, setVerificationLoadingIds] = useState([]);
  const navigate = useNavigate();
  const { id: routePropertyId } = useParams();
  const propertyIdFromRoute = routePropertyId ? Number(routePropertyId) : null;

  const getImageSrc = useCallback(
    (image) => {
      if (!image) return null;
      if (image.image_url) {
        return normalizeMediaUrl(image.image_url);
      }
      if (image.url) {
        return normalizeMediaUrl(image.url);
      }
      let path = image.file_path || image.path;
      if (!path) return null;
      if (path.startsWith('http')) return normalizeMediaUrl(path);
      path = path.replace(/^public\//, '').replace(/^\/?storage\//, '');
      return storageUrl(path);
    },
    []
  );

  const subscriptionBuyers = useMemo(() => {
    const map = new Map();
    monthlyTransactions.forEach((tx) => {
      const key = tx.payer_user_id || `tx-${tx.id}`;
      const current = map.get(key) || {
        id: key,
        name: tx.payer?.name || 'Unknown buyer',
        email: tx.payer?.email || '—',
        totalAmount: 0,
        reporterShare: 0,
        platformShare: 0,
        transactions: 0,
        lastPurchase: null,
      };

      current.totalAmount += Number(tx.amount || 0);
      current.reporterShare += Number(tx.reporter_pool_share || 0);
      current.platformShare += Number(tx.platform_share || 0);
      current.transactions += 1;
      const purchaseDate = tx.created_at ? new Date(tx.created_at) : null;
      if (purchaseDate && (!current.lastPurchase || purchaseDate > current.lastPurchase)) {
        current.lastPurchase = purchaseDate;
      }

      map.set(key, current);
    });

    return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [monthlyTransactions]);

  const visibleProperties = useMemo(() => {
    if (focusedProperty) {
      const exists = properties.some((prop) => Number(prop.id) === Number(focusedProperty.id));
      if (!exists) {
        return [focusedProperty, ...properties];
      }
    }
    return properties;
  }, [focusedProperty, properties]);

  const propertyMarkers = useMemo(() => (
    (allPropertiesForMap || [])
      .filter((property) => property?.latitude != null && property?.longitude != null)
      .map((property) => {
        const status = property.status === 'approved'
          ? 'approved'
          : property.status === 'pending'
            ? 'pending'
            : 'other';
        const color = status === 'approved' ? '#16A34A' : status === 'pending' ? '#DC2626' : '#6B7280';
        return {
          id: property.id,
          lat: parseFloat(property.latitude),
          lng: parseFloat(property.longitude),
          title: property.title || `Property ${property.id}`,
          description: property.address || property.city || '',
          price: property.price,
          color,
          status,
          onEdit: async () => {
            const id = property.id;
            setFocusedPropertyId(id);
            setActiveTab('properties');
            await loadPropertyById(id);
            setTimeout(() => {
              document.getElementById(`property-card-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 250);
          },
        };
      })
  ), [allPropertiesForMap]);

  const propertyMapBounds = useMemo(() => {
    if (!propertyMarkers.length) return null;
    let minLat = propertyMarkers[0].lat;
    let maxLat = propertyMarkers[0].lat;
    let minLng = propertyMarkers[0].lng;
    let maxLng = propertyMarkers[0].lng;
    propertyMarkers.forEach((marker) => {
      minLat = Math.min(minLat, marker.lat);
      maxLat = Math.max(maxLat, marker.lat);
      minLng = Math.min(minLng, marker.lng);
      maxLng = Math.max(maxLng, marker.lng);
    });
    return [[minLat, minLng], [maxLat, maxLng]];
  }, [propertyMarkers]);

  const loadStatistics = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // Load property statistics
      const propertiesResponse = await fetch(apiUrl('/admin/properties/statistics'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      
      if (!propertiesResponse.ok) {
        if (propertiesResponse.status === 401) {
          // Token expired or invalid, redirect to login
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          navigate('/admin/login');
          return;
        }
        throw new Error(`Properties statistics failed: ${propertiesResponse.status}`);
      }
      
      const propertiesData = await propertiesResponse.json();
      
      // Load earnings statistics
      const earningsResponse = await fetch(apiUrl('/admin/earnings/statistics'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      
      if (!earningsResponse.ok) {
        if (earningsResponse.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          navigate('/admin/login');
          return;
        }
        throw new Error(`Earnings statistics failed: ${earningsResponse.status}`);
      }
      
      const earningsData = await earningsResponse.json();
      
      setStatistics({
        properties: propertiesData.data,
        earnings: earningsData.data
      });
    } catch (error) {
      console.error('Error loading statistics:', error);
      // If it's a network error or other issue, don't redirect immediately
      // Let the user see the error and try again
    }
  }, [navigate]);

  const loadRevenueStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const r = await fetch(apiUrl('/admin/revenue/statistics'), {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      const j = await r.json();
      if (j.success) {
        setRevenueStats(j.data);
      }
    } catch (e) {
      console.error('Error loading revenue stats:', e);
    }
  }, []);

  const loadRevenuePreviewUsers = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const r = await fetch(apiUrl('/admin/revenue/preview-current-users'), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      const j = await r.json();
      if (j.success) {
        setRevenuePreviewUsers(j.data);
      } else {
        setRevenuePreviewUsers(null);
      }
    } catch (e) {
      setRevenuePreviewUsers(null);
    }
  }, []);

  const loadRevenuePreview = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const r = await fetch(apiUrl('/admin/revenue/preview-current'), {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      const j = await r.json();
      if (j.success) {
        setRevenuePreview(j.data);
      }
    } catch (e) {
      console.error('Error loading revenue preview:', e);
    }
  }, []);

  const loadDistributions = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const r = await fetch(apiUrl('/admin/revenue/distributions'), {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      const j = await r.json();
      if (j.success) {
        const list = j.data.data || [];
        setDistributions(list);
        // Default to the latest distribution
        if (list.length && !selectedDistributionId) {
          setSelectedDistributionId(list[0].id);
        }
      }
    } catch (e) {
      console.error('Error loading distributions:', e);
    }
  }, [selectedDistributionId]);

  // eslint-disable-next-line no-unused-vars
  const calculateDistribution = async (year, month) => {
    const token = localStorage.getItem('admin_token');
    try {
      const r = await fetch(apiUrl('/admin/revenue/calculate-distribution'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ year: Number(year), month: Number(month) }),
      });
      const j = await r.json();
      if (j.success) {
        toast.success('Monthly distribution calculated');
        await loadRevenueStats();
        await loadDistributions();
      } else {
        toast.error(j.message || 'Failed to calculate distribution');
      }
    } catch (e) {
      console.error('Error calculating distribution:', e);
      toast.error('Failed to calculate distribution');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const distributePayments = async (distributionId) => {
    const token = localStorage.getItem('admin_token');
    try {
      const r = await fetch(apiUrl(`/admin/revenue/distribute-payments/${distributionId}`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      const j = await r.json();
      if (j.success) {
        toast.success('Payments distributed');
        await loadRevenueStats();
        await loadDistributions();
      } else {
        toast.error(j.message || 'Failed to distribute payments');
      }
    } catch (e) {
      console.error('Error distributing payments:', e);
      toast.error('Failed to distribute payments');
    }
  };

  const loadMaintenanceSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl('/admin/maintenance'), {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      const data = await response.json();
      if (response.ok && data?.success) {
        setMaintenanceSettings({
          enabled: !!data.data?.enabled,
          updated_at: data.data?.updated_at ?? null,
        });
      }
    } catch (_e) {
      // best-effort
    }
  }, []);

  const setMaintenanceEnabled = useCallback(async (enabled) => {
    try {
      setMaintenanceBusy(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl('/admin/maintenance'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !!enabled }),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        toast.error(data?.message || 'Failed to update maintenance mode');
        return;
      }
      setMaintenanceSettings({
        enabled: !!data.data?.enabled,
        updated_at: data.data?.updated_at ?? null,
      });
      toast.success(`Maintenance mode ${data.data?.enabled ? 'enabled' : 'disabled'}`);
    } catch (_e) {
      toast.error('Failed to update maintenance mode');
    } finally {
      setMaintenanceBusy(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    const adminData = localStorage.getItem('admin_user');
    
    if (!token || !adminData) {
      navigate('/admin/login');
      return;
    }

    // Validate token by checking admin profile
    try {
      const response = await fetch(apiUrl('/admin/auth/profile'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        // Token is invalid, clear storage and redirect to login
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin/login');
        return;
      }

      const data = await response.json();
      if (data.success) {
        setAdmin(data.data);
        setLoading(false);
        // Load statistics after authentication is confirmed
        loadStatistics();
        loadMaintenanceSettings();
      } else {
        // Invalid response, redirect to login
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Token validation error:', error);
      // Network error or invalid token, redirect to login
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin/login');
    }
  }, [navigate, loadStatistics, loadMaintenanceSettings]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loadProperties = async (overrides = {}) => {
    try {
      setPropertiesLoading(true);
      const token = localStorage.getItem('admin_token');
      const page = overrides.page ?? propertyPage;
      const status = overrides.status ?? propertyStatusFilter;
      const revised = overrides.revised ?? propertyRevisionFilter;
      const agentContact = overrides.agentContact ?? propertyAgentContactFilter;
      const params = new URLSearchParams();
      params.set('page', String(page));
      if (status && status !== 'all') {
        params.set('status', status);
      }
      if (revised && revised !== 'all') {
        params.set('is_revised', revised === 'revised' ? '1' : '0');
      }
      if (agentContact === 'agent_only') {
        params.set('allow_agent_contact', '1');
      }

      const response = await fetch(`${apiUrl('/admin/properties')}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      
      const data = await response.json();
      if (data.success) {
        const pageData = data.data || {};
        setProperties(pageData.data || []);
        setPropertyPagination({
          currentPage: pageData.current_page || page,
          lastPage: pageData.last_page || 1,
          total: pageData.total || 0,
          perPage: pageData.per_page || propertyPagination.perPage,
        });
        setPropertyPage(pageData.current_page || page);
      }
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setPropertiesLoading(false);
    }
  };

  // Map always shows ALL properties (no filters), independent of the paginated/filtered list below.
  const loadPropertiesForMap = useCallback(async () => {
    try {
      setMapMarkersLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl('/admin/properties/map'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setAllPropertiesForMap(data.data);
      } else {
        setAllPropertiesForMap([]);
      }
    } catch (error) {
      console.error('Error loading properties for map:', error);
      setAllPropertiesForMap([]);
    } finally {
      setMapMarkersLoading(false);
    }
  }, []);

  const loadPropertyById = async (id) => {
    if (!id) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/properties/${id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success && data.data) {
        setFocusedProperty(data.data);
      } else {
        setFocusedProperty(null);
      }
    } catch (error) {
      console.error('Error loading property detail:', error);
      setFocusedProperty(null);
    }
  };

  useEffect(() => {
    if (propertyIdFromRoute && activeTab !== 'properties') {
      setActiveTab('properties');
    }
  }, [propertyIdFromRoute, activeTab]);

  useEffect(() => {
    if (propertyIdFromRoute) {
      setFocusedPropertyId(propertyIdFromRoute);
      loadPropertyById(propertyIdFromRoute);
      loadProperties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyIdFromRoute]);

  useEffect(() => {
    if (!propertyIdFromRoute) return;
    const exists = visibleProperties.some((prop) => Number(prop.id) === Number(propertyIdFromRoute));
    if (!exists && !propertiesLoading) {
      toast.error('Property not found');
    }
  }, [propertyIdFromRoute, visibleProperties, propertiesLoading]);

  useEffect(() => {
    if (activeTab !== 'properties') return;
    loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, propertyStatusFilter, propertyRevisionFilter, propertyAgentContactFilter, propertyPage]);

  useEffect(() => {
    if (activeTab !== 'properties') return;
    loadPropertiesForMap();
  }, [activeTab, loadPropertiesForMap]);

  // Commissions removed from admin UI

  const loadEarnings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl('/admin/earnings'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      
      const data = await response.json();
      if (data.success) {
        setEarnings(data.data.data);
      }
    } catch (error) {
      console.error('Error loading earnings:', error);
    }
  };

  const loadMonthlyRevenueOverview = useCallback(async (year, month) => {
    try {
      setMonthlyLoading(true);
      setMonthlyError(null);

      const token = localStorage.getItem('admin_token');
      // For "Subscription income overview" we want subscription payments, not property contact revenue.
      const params = new URLSearchParams({
        month: String(month),
        year: String(year),
        source: 'subscription',
        status: 'completed',
      }).toString();
      const response = await fetch(`${apiUrl('/admin/revenue/transactions')}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to load monthly revenue');
      }

      const rawList = data.data?.data ?? data.data ?? [];
      const items = Array.isArray(rawList) ? rawList : [];
      const resolvedItems = items.filter((tx) => (tx.status ? tx.status === 'completed' : true));

      const summary = resolvedItems.reduce((acc, tx) => {
        const amount = Number(tx.amount || 0);
        const platformShare = Number(tx.platform_share || 0);
        const reporterShare = Number(tx.reporter_pool_share || 0);
        return {
          totalRevenue: acc.totalRevenue + amount,
          platformShare: acc.platformShare + platformShare,
          reporterShare: acc.reporterShare + reporterShare,
        };
      }, { totalRevenue: 0, platformShare: 0, reporterShare: 0 });

      setMonthlyTransactions(resolvedItems);
      setMonthlySummary({
        totalRevenue: summary.totalRevenue,
        platformShare: summary.platformShare,
        reporterShare: summary.reporterShare,
        transactionCount: resolvedItems.length,
      });
    } catch (error) {
      console.error('Error loading monthly revenue overview:', error);
      setMonthlyTransactions([]);
      setMonthlySummary(null);
      setMonthlyError(error.message || 'Failed to load monthly overview');
    } finally {
      setMonthlyLoading(false);
    }
  }, []);

  const loadSubscriptionPlans = useCallback(async () => {
    try {
      setPlansLoading(true);
      setPlansError(null);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl('/admin/subscription-plans'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to load subscription plans');
      }
      const plans = Array.isArray(data.data) ? data.data : [];
      setSubscriptionPlans(plans);
      setPlanInputs(() => {
        const next = {};
        plans.forEach((plan) => {
          next[plan.id] = {
            price: plan.price,
          };
        });
        return next;
      });
    } catch (error) {
      console.error('Error loading subscription plans:', error);
      setPlansError(error.message || 'Failed to load subscription plans');
      toast.error(error.message || 'Failed to load subscription plans');
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const loadTermsSettings = useCallback(async () => {
    try {
      setTermsLoading(true);
      setTermsError(null);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl('/admin/terms'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to load terms');
      }

      const payload = data.data || {};
      setTermsSettings(payload);
      setTermsInput({
        land_seeker: payload.land_seeker || '',
        land_reporter: payload.land_reporter || '',
      });
    } catch (error) {
      console.error('Error loading terms:', error);
      setTermsError(error.message || 'Failed to load terms');
    } finally {
      setTermsLoading(false);
    }
  }, []);

  const saveTermsSettings = async () => {
    try {
      setTermsSaving(true);
      setTermsError(null);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl('/admin/terms'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          land_seeker: termsInput.land_seeker,
          land_reporter: termsInput.land_reporter,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to update terms');
      }
      toast.success('Terms updated');
      setTermsSettings(data.data || {});
    } catch (error) {
      console.error('Error saving terms:', error);
      toast.error(error.message || 'Failed to update terms');
      setTermsError(error.message || 'Failed to update terms');
    } finally {
      setTermsSaving(false);
    }
  };

  const loadInvoiceSettings = useCallback(async () => {
    try {
      setInvoiceLoading(true);
      setInvoiceError(null);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl('/admin/invoice-settings'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to load invoice settings');
      }
      const payload = data.data || {};
      setInvoiceSettings(payload);
      setInvoiceInput({
        company_name: payload.company_name || '',
        address_line1: payload.address_line1 || '',
        address_line2: payload.address_line2 || '',
        zip: payload.zip || '',
        city: payload.city || '',
        state: payload.state || '',
        country: payload.country || '',
        tax_id: payload.tax_id || '',
        email: payload.email || '',
      });
    } catch (error) {
      console.error('Error loading invoice settings:', error);
      setInvoiceError(error.message || 'Failed to load invoice settings');
    } finally {
      setInvoiceLoading(false);
    }
  }, []);

  const loadAreaSearches = useCallback(async () => {
    try {
      setAreaSearchesLoading(true);
      setAreaSearchesError(null);
      const res = await adminAPI.get('/admin/area-searches');
      const data = res.data?.data || {};
      setAreaSearches({
        searches: data.searches || [],
        top_areas: data.top_areas || [],
      });
    } catch (error) {
      console.error('Error loading area searches:', error);
      setAreaSearchesError(error.response?.data?.message || error.message || 'Failed to load area searches');
    } finally {
      setAreaSearchesLoading(false);
    }
  }, []);

  const saveInvoiceSettings = async () => {
    try {
      setInvoiceSaving(true);
      setInvoiceError(null);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl('/admin/invoice-settings'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(invoiceInput),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to update invoice settings');
      }
      toast.success('Invoice settings updated');
      setInvoiceSettings(data.data || {});
    } catch (error) {
      console.error('Error saving invoice settings:', error);
      toast.error(error.message || 'Failed to update invoice settings');
      setInvoiceError(error.message || 'Failed to update invoice settings');
    } finally {
      setInvoiceSaving(false);
    }
  };

  const handlePlanInputChange = (planId, value) => {
    setPlanInputs((prev) => ({
      ...prev,
      [planId]: {
        ...(prev[planId] || {}),
        price: value,
      },
    }));
  };

  const handleSavePlanPrice = useCallback(async (planId) => {
    const inputPrice = planInputs[planId]?.price ?? '';
    const numericPrice = Number(inputPrice);
    if (Number.isNaN(numericPrice)) {
      toast.error('Please enter a valid price');
      return;
    }
    if (numericPrice < 0) {
      toast.error('Price cannot be negative');
      return;
    }

    setSavingPlanId(planId);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/subscription-plans/${planId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ price: numericPrice }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to update subscription plan');
      }
      toast.success('Subscription plan updated');
      const updatedPlan = data.data;
      setSubscriptionPlans((prev) =>
        prev.map((plan) => (plan.id === planId ? updatedPlan : plan))
      );
      setPlanInputs((prev) => ({
        ...prev,
        [planId]: {
          ...(prev[planId] || {}),
          price: updatedPlan?.price ?? numericPrice,
        },
      }));
    } catch (error) {
      console.error('Error updating subscription plan:', error);
      toast.error(error.message || 'Failed to update subscription plan');
    } finally {
      setSavingPlanId(null);
    }
  }, [planInputs]);

  useEffect(() => {
    if (activeTab === 'earnings') {
      loadMonthlyRevenueOverview(selectedPeriod.year, selectedPeriod.month);
    }
  }, [activeTab, selectedPeriod, loadMonthlyRevenueOverview]);

  useEffect(() => {
    if (activeTab === 'earnings') {
      loadSubscriptionPlans();
    }
  }, [activeTab, loadSubscriptionPlans]);

  useEffect(() => {
    if (activeTab === 'terms') {
      loadTermsSettings();
    }
  }, [activeTab, loadTermsSettings]);

  useEffect(() => {
    if (activeTab === 'invoice') {
      loadInvoiceSettings();
    }
  }, [activeTab, loadInvoiceSettings]);

  const handleApproveProperty = async (propertyId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/properties/${propertyId}/approve`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify({}),
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Property approved successfully!');
        loadProperties();
        loadPropertiesForMap();
        loadStatistics();
      } else {
        toast.error(data.message || 'Failed to approve property');
      }
    } catch (error) {
      console.error('Error approving property:', error);
      toast.error('Failed to approve property');
    }
  };

  const handleRejectProperty = async (propertyId, reason) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/properties/${propertyId}/reject`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ rejection_reason: reason }),
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Property rejected successfully!');
        loadProperties();
        loadPropertiesForMap();
        loadStatistics();
      } else {
        toast.error(data.message || 'Failed to reject property');
      }
    } catch (error) {
      console.error('Error rejecting property:', error);
      toast.error('Failed to reject property');
    }
  };

  const handleUpdateProperty = async (propertyId, updateData) => {
    try {
      console.log('Updating property with data:', updateData);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/properties/${propertyId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(updateData),
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Property updated successfully!');
        loadProperties();
        loadPropertiesForMap();
      } else {
        console.error('Update failed:', data);
        if (data.errors) {
          const errorMessages = Object.entries(data.errors)
            .map(([field, messages]) => `${field}: ${(messages || []).join(' ')}`)
            .join(' | ');
          toast.error(`Validation failed: ${errorMessages}`);
        } else {
          toast.error(data.message || 'Failed to update property');
        }
      }
    } catch (error) {
      console.error('Error updating property:', error);
      toast.error('Failed to update property');
    }
  };

  const handleCreateVerificationLink = async (propertyId) => {
    try {
      setVerificationLoadingIds((prev) => [...new Set([...prev, propertyId])]);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/properties/${propertyId}/verification-link`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        toast.error(data.message || 'Failed to generate verification link');
        return;
      }
      const link = data.data?.link;
      if (link) {
        setVerificationLinks((prev) => ({ ...prev, [propertyId]: link }));
        try {
          await navigator.clipboard.writeText(link);
          toast.success('Verification link copied to clipboard');
        } catch (err) {
          toast.success('Verification link generated');
        }
      }
    } catch (error) {
      console.error('Error creating verification link:', error);
      toast.error('Failed to generate verification link');
    } finally {
      setVerificationLoadingIds((prev) => prev.filter((id) => id !== propertyId));
    }
  };

  const handleDeletePropertyMedia = async (propertyId, media) => {
    const confirmed = window.confirm(
      `Delete this ${media.file_type === 'video' ? 'video' : 'image'}?\n\nFile: ${media.file_path}\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/properties/${propertyId}/media/${media.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`${media.file_type === 'video' ? 'Video' : 'Image'} deleted`);
        loadProperties();
        loadPropertiesForMap();
        loadStatistics();
      } else {
        toast.error(data.message || 'Failed to delete media');
      }
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Failed to delete media');
    }
  };

  const handleUploadPropertyMedia = async (propertyId, files, onProgress) => {
    if (!files || files.length === 0) return;
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('media[]', file);
      });

      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', apiUrl(`/admin/properties/${propertyId}/media`));
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable && onProgress) {
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress(percent);
          }
        };
        xhr.onload = () => {
          try {
            const json = JSON.parse(xhr.responseText || '{}');
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(json);
            } else {
              reject(json);
            }
          } catch (error) {
            reject(error);
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(formData);
      });

      if (data.success) {
        toast.success('Media uploaded successfully');
        loadProperties();
        loadPropertiesForMap();
        loadStatistics();
      } else {
        if (data.errors) {
          const errorMessages = Object.entries(data.errors)
            .map(([field, messages]) => `${field}: ${(messages || []).join(' ')}`)
            .join(' | ');
          toast.error(`Upload failed: ${errorMessages}`);
        } else {
          toast.error(data.message || 'Failed to upload media');
        }
      }
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error(error?.message || 'Failed to upload media');
    }
  };

  const handleDeleteProperty = async (propertyId, propertyTitle) => {
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete "${propertyTitle}"?\n\nThis action cannot be undone and will remove:\n- All property images\n- All commission transactions\n- All contact messages\n- All associated data`
    );
    
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/properties/${propertyId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Property deleted successfully!');
        loadProperties();
        loadPropertiesForMap();
        loadStatistics();
      } else {
        toast.error(data.message || 'Failed to delete property');
      }
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error('Failed to delete property');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const loadAdminAccounts = useCallback(async () => {
    // super admin only
    if (admin?.role !== 'super_admin') return;

    setAdminsLoading(true);
    setAdminsError(null);
    try {
      const token = localStorage.getItem('admin_token');
      const r = await fetch(apiUrl('/admin/auth/admins'), {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      const j = await r.json();
      if (!r.ok || !j?.success) {
        setAdminsError(j?.message || 'Failed to load admin accounts');
        setAdminAccounts([]);
        return;
      }
      setAdminAccounts(Array.isArray(j.data) ? j.data : []);
    } catch (e) {
      console.error('Error loading admin accounts:', e);
      setAdminsError('Failed to load admin accounts');
      setAdminAccounts([]);
    } finally {
      setAdminsLoading(false);
    }
  }, [admin?.role]);

  const openEditAdmin = (a) => {
    setEditingAdmin(a);
    setAdminEditForm({
      name: a?.name || '',
      email: a?.email || '',
      role: a?.role || 'admin',
      is_active: !!a?.is_active,
      password: '',
      password_confirmation: '',
    });
  };

  const saveAdminEdits = async () => {
    if (!editingAdmin?.id) return;
    const token = localStorage.getItem('admin_token');

    const payload = {
      name: adminEditForm.name,
      email: adminEditForm.email,
      role: adminEditForm.role,
      is_active: !!adminEditForm.is_active,
    };

    if (adminEditForm.password) {
      payload.password = adminEditForm.password;
      payload.password_confirmation = adminEditForm.password_confirmation;
    }

    try {
      const r = await fetch(apiUrl(`/admin/auth/admins/${editingAdmin.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!r.ok || !j?.success) {
        toast.error(j?.message || 'Failed to update admin');
        return;
      }
      toast.success('Admin updated');
      setEditingAdmin(null);
      await loadAdminAccounts();
    } catch (e) {
      console.error('Error updating admin:', e);
      toast.error('Failed to update admin');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-700">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome back, {admin?.name}</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Role: {admin?.role}</span>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'properties', label: 'Properties' },
              { id: 'upload', label: 'Upload Property' },
              // Commissions tab removed
              { id: 'earnings', label: 'Earnings' },
              { id: 'revenue', label: 'Revenue' },
              { id: 'users', label: 'Users' },
              { id: 'searches', label: 'Searches' },
              { id: 'background', label: 'Background Images' },
              { id: 'terms', label: 'Terms of Service' },
              { id: 'invoice', label: 'Invoice Settings' },
              ...(admin?.role === 'super_admin' ? [{ id: 'admins', label: 'Admin Accounts' }] : []),
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'properties') {
                    loadProperties();
                    loadPropertiesForMap();
                  }
                  if (tab.id === 'earnings') {
                    loadEarnings();
                  }
                  if (tab.id === 'revenue') { loadRevenueStats(); loadDistributions(); loadRevenuePreview(); loadRevenuePreviewUsers(); }
                  if (tab.id === 'searches') loadAreaSearches();
                  if (tab.id === 'admins') { loadAdminAccounts(); }
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">P</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Total Properties</dt>
                        <dd className="text-lg font-medium text-gray-900">{statistics.properties?.total_properties || 0}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">⏳</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Pending Properties</dt>
                        <dd className="text-lg font-medium text-gray-900">{statistics.properties?.pending_properties || 0}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">✓</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Approved Properties</dt>
                        <dd className="text-lg font-medium text-gray-900">{statistics.properties?.approved_properties || 0}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              {/* Commissions card removed */}

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">💰</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Total Earnings</dt>
                        <dd className="text-lg font-medium text-gray-900">${statistics.earnings?.total_earnings || 0}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">⏳</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Pending Payouts</dt>
                        <dd className="text-lg font-medium text-gray-900">${statistics.earnings?.total_pending_payouts || 0}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-indigo-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">👥</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Users with Earnings</dt>
                        <dd className="text-lg font-medium text-gray-900">{statistics.earnings?.total_users_with_earnings || 0}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Maintenance Mode */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6 flex items-start justify-between gap-6">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Maintenance Mode</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    When enabled, all visitors and non-admin users are redirected to <span className="font-mono">/maintenance</span>. Admin routes stay accessible.
                  </p>
                  {maintenanceSettings.updated_at && (
                    <p className="mt-1 text-xs text-gray-500">
                      Last changed: {new Date(maintenanceSettings.updated_at).toLocaleString()}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${maintenanceSettings.enabled ? 'text-red-700' : 'text-green-700'}`}>
                    {maintenanceSettings.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    type="button"
                    disabled={maintenanceBusy}
                    onClick={() => setMaintenanceEnabled(!maintenanceSettings.enabled)}
                    className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
                      maintenanceSettings.enabled ? 'bg-gray-700 hover:bg-gray-800' : 'bg-red-600 hover:bg-red-700'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {maintenanceBusy
                      ? 'Saving...'
                      : maintenanceSettings.enabled
                        ? 'Disable maintenance'
                        : 'Enable maintenance'}
                  </button>
                </div>
              </div>
            </div>

            {/* Recent Properties */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Recent Properties</h3>
                <div className="space-y-4">
                  {statistics.properties?.recent_properties?.map((property) => {
                    const firstImage =
                      property.images?.find((img) => img?.is_primary) ||
                      property.images?.[0] ||
                      null;
                    const firstImageSrc = getImageSrc(firstImage);
                    return (
                    <div key={property.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        {firstImageSrc ? (
                          <PropertyImageThumbnail
                            src={firstImageSrc}
                            alt={property.title}
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
                            No Image
                          </div>
                        )}
                        <div>
                          <h4 className="font-medium text-gray-900">{property.title}</h4>
                          <p className="text-sm text-gray-500">{property.address}</p>
                          <p className="text-sm text-gray-500">{formatPrice(property.price, property.currency)} • {property.property_type}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {(() => {
                          const isReportedSold = property.status === 'pending' && property.rejection_reason === 'reported_sold';
                          const label = isReportedSold ? 'reported_sold' : property.status;
                          const badgeClass =
                            label === 'approved' ? 'bg-green-100 text-green-800' :
                            label === 'sold' ? 'bg-red-600 text-white' :
                            label === 'rejected' ? 'bg-red-100 text-red-800' :
                            isReportedSold ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800';
                          return (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeClass}`}>
                              {label}
                        </span>
                          );
                        })()}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'properties' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Property Management</h3>
              <div className="mb-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
                  <div className="text-sm text-gray-600">
                    Property map overview
                    {!mapMarkersLoading && propertyMarkers.length > 0 && (
                      <span className="ml-2 text-gray-500">({propertyMarkers.length} with location)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: '#16A34A' }} />
                      Approved
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: '#DC2626' }} />
                      Pending
                    </div>
                  </div>
                </div>
                {mapMarkersLoading ? (
                  <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent" />
                    Loading all properties for map…
                  </div>
                ) : propertyMarkers.length ? (
                  <MapComponent
                    height="360px"
                    markers={propertyMarkers}
                    bounds={propertyMapBounds}
                    showControls={false}
                    allowClick={false}
                  />
                ) : (
                  <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">
                    No coordinates available yet.
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label htmlFor="property-status-filter" className="text-sm text-gray-600">
                      Status filter
                    </label>
                    <select
                      id="property-status-filter"
                      value={propertyStatusFilter}
                      onChange={(e) => {
                        const nextStatus = e.target.value;
                        setPropertyStatusFilter(nextStatus);
                        setPropertyPage(1);
                      }}
                      className="px-2 py-1 text-sm border rounded bg-white"
                    >
                      <option value="all">All</option>
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="property-revision-filter" className="text-sm text-gray-600">
                      Revision filter
                    </label>
                    <select
                      id="property-revision-filter"
                      value={propertyRevisionFilter}
                      onChange={(e) => {
                        const nextRevision = e.target.value;
                        setPropertyRevisionFilter(nextRevision);
                        setPropertyPage(1);
                      }}
                      className="px-2 py-1 text-sm border rounded bg-white"
                    >
                      <option value="all">All</option>
                      <option value="revised">Revised</option>
                      <option value="not_revised">Not revised</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="property-agent-contact-filter" className="text-sm text-gray-600">
                      {t('admin.propertyAgentContactFilter')}
                    </label>
                    <select
                      id="property-agent-contact-filter"
                      value={propertyAgentContactFilter}
                      onChange={(e) => {
                        setPropertyAgentContactFilter(e.target.value);
                        setPropertyPage(1);
                      }}
                      className="px-2 py-1 text-sm border rounded bg-white"
                    >
                      <option value="all">{t('admin.propertyFilterAll')}</option>
                      <option value="agent_only">{t('admin.propertyFilterAgentOnly')}</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      const token = localStorage.getItem('admin_token');
                      const params = new URLSearchParams();
                      if (propertyStatusFilter && propertyStatusFilter !== 'all') params.set('status', propertyStatusFilter);
                      if (propertyRevisionFilter && propertyRevisionFilter !== 'all') params.set('is_revised', propertyRevisionFilter === 'revised' ? '1' : '0');
                      if (propertyAgentContactFilter === 'agent_only') params.set('allow_agent_contact', '1');
                      const url = `${apiUrl('/admin/properties/export')}?${params.toString()}`;
                      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
                      if (!res.ok) return;
                      const blob = await res.blob();
                      const disposition = res.headers.get('Content-Disposition');
                      const match = disposition && disposition.match(/filename="?([^";\n]+)"?/);
                      const filename = match ? match[1] : `properties-export-${new Date().toISOString().slice(0, 10)}.csv`;
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = filename;
                      a.click();
                      URL.revokeObjectURL(a.href);
                    }}
                    className="px-3 py-1.5 text-sm border rounded bg-white hover:bg-gray-50"
                  >
                    {t('admin.exportPropertiesCsv')}
                  </button>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>
                    {propertyPagination.total || 0} total
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPropertyPage((prev) => Math.max(1, prev - 1))}
                      disabled={propertyPage <= 1}
                      className="px-2 py-1 text-xs border rounded disabled:opacity-50"
                    >
                      ← Prev
                    </button>
                    <span className="text-xs">
                      Page {propertyPage} of {propertyPagination.lastPage || 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPropertyPage((prev) => Math.min(propertyPagination.lastPage || 1, prev + 1))}
                      disabled={propertyPage >= (propertyPagination.lastPage || 1)}
                      className="px-2 py-1 text-xs border rounded disabled:opacity-50"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                {propertiesLoading && (
                  <div className="text-sm text-gray-500">Loading properties…</div>
                )}
                {!propertiesLoading && visibleProperties.map((property) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    onApprove={handleApproveProperty}
                    onReject={handleRejectProperty}
                    onUpdate={handleUpdateProperty}
                    onDelete={handleDeleteProperty}
                    onDeleteMedia={handleDeletePropertyMedia}
                    onUploadMedia={handleUploadPropertyMedia}
                    getImageSrc={getImageSrc}
                    onCreateVerificationLink={handleCreateVerificationLink}
                    verificationLink={verificationLinks[property.id]}
                    isCreatingVerificationLink={verificationLoadingIds.includes(property.id)}
                    autoExpand={Boolean(focusedPropertyId) && Number(property.id) === Number(focusedPropertyId)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'upload' && (
          <AdminPropertyUpload onPropertyCreated={() => {
            loadProperties();
            loadStatistics();
          }} />
        )}

        {/* Commissions section removed */}

        {activeTab === 'earnings' && (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Subscription income overview</h3>
                    <p className="text-sm text-gray-500">Summary of subscription revenue and distribution for the selected month.</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setSelectedPeriod((prev) => {
                        const date = new Date(prev.year, prev.month - 2, 1);
                        return { year: date.getFullYear(), month: date.getMonth() + 1 };
                      })}
                      className="px-3 py-1 rounded border text-sm hover:bg-gray-50"
                    >
                      ← Previous
                    </button>
                    <div className="text-sm font-medium text-gray-700">
                      {new Date(selectedPeriod.year, selectedPeriod.month - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedPeriod((prev) => {
                        const date = new Date(prev.year, prev.month, 1);
                        return { year: date.getFullYear(), month: date.getMonth() + 1 };
                      })}
                      className="px-3 py-1 rounded border text-sm hover:bg-gray-50"
                    >
                      Next →
                    </button>
                  </div>
                </div>

                {monthlyLoading ? (
                  <div className="text-sm text-gray-500">Loading monthly overview…</div>
                ) : monthlyError ? (
                  <div className="text-sm text-red-600">{monthlyError}</div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-4 text-sm text-gray-700">
                    <div className="bg-gray-50 p-4 rounded">
                      <div className="text-xs uppercase text-gray-500">Total revenue</div>
                      <div className="text-xl font-semibold text-gray-900">${money(monthlySummary?.totalRevenue)}</div>
                      <div className="text-xs text-gray-500 mt-1">Completed subscriptions this month</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded">
                      <div className="text-xs uppercase text-gray-500">Reporter pool share</div>
                      <div className="text-xl font-semibold text-green-600">${money(monthlySummary?.reporterShare)}</div>
                      <div className="text-xs text-gray-500 mt-1">Amount heading to land reporters</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded">
                      <div className="text-xs uppercase text-gray-500">Platform share</div>
                      <div className="text-xl font-semibold text-indigo-600">${money(monthlySummary?.platformShare)}</div>
                      <div className="text-xs text-gray-500 mt-1">Remaining platform revenue</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded">
                      <div className="text-xs uppercase text-gray-500">Subscriptions</div>
                      <div className="text-xl font-semibold text-gray-900">{monthlySummary?.transactionCount || 0}</div>
                      <div className="text-xs text-gray-500 mt-1">Completed purchases this month</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <SubscriptionBuyersPanel
              transactions={monthlyTransactions}
              buyers={subscriptionBuyers}
              loading={monthlyLoading}
              month={selectedPeriod.month}
              year={selectedPeriod.year}
            />

            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Subscription plans</h3>
                    <p className="text-sm text-gray-500">
                      Update the prices that land seekers see on the subscription page.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={loadSubscriptionPlans}
                    disabled={plansLoading}
                    className="px-3 py-1 rounded border text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Refresh
                  </button>
                </div>
                {plansError && (
                  <div className="text-sm text-red-600">{plansError}</div>
                )}
                {plansLoading ? (
                  <div className="text-sm text-gray-500">Loading subscription plans…</div>
                ) : subscriptionPlans.length === 0 ? (
                  <div className="text-sm text-gray-500">No subscription plans configured yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-3 py-2">Plan</th>
                          <th className="text-left px-3 py-2">Billing</th>
                          <th className="text-left px-3 py-2">Max properties</th>
                          <th className="text-left px-3 py-2">Price (USD)</th>
                          <th className="text-right px-3 py-2">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptionPlans.map((plan) => {
                          const currentValue = planInputs[plan.id]?.price ?? plan.price ?? '';
                          return (
                            <tr key={plan.id} className="border-t">
                              <td className="px-3 py-2">
                                <div className="font-medium text-gray-900">{plan.name}</div>
                                {plan.description && (
                                  <div className="text-xs text-gray-500">{plan.description}</div>
                                )}
                                {!plan.is_active && (
                                  <div className="text-xs text-red-600 mt-1">Inactive</div>
                                )}
                              </td>
                              <td className="px-3 py-2 capitalize">{plan.billing_cycle}</td>
                              <td className="px-3 py-2">
                                {typeof plan.max_properties === 'number'
                                  ? `${plan.max_properties} listings`
                                  : 'Unlimited'}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm text-gray-500">$</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={currentValue}
                                    onChange={(e) => handlePlanInputChange(plan.id, e.target.value)}
                                    className="w-28 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => handleSavePlanPrice(plan.id)}
                                  disabled={savingPlanId === plan.id}
                                  className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {savingPlanId === plan.id ? 'Saving…' : 'Save price'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Reporter earnings & payouts</h3>
                  <p className="text-sm text-gray-500">Monitor balances and process payouts.</p>
                </div>
                <div className="space-y-4">
                  {earnings.map((user) => (
                    <EarningsCard key={user.id} user={user} />
                  ))}
                  {earnings.length === 0 && (
                    <div className="text-sm text-gray-500">No reporter earnings yet.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'revenue' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6 space-y-6">
              {/* Top stats */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Reward overview</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700">
                    <div>Total Turnover: ${money((revenueStats?.total_revenue))}</div>
                    <div>Platform Earnings: ${money(revenueStats?.platform_earnings)}</div>
                    <div>Reporter Pool Total: ${money(revenueStats?.reporter_pool_total)}</div>
                  </div>
                  {revenueStats?.current_reporter_pool_share_percent !== undefined && (
                    <div className="mt-2 text-xs text-gray-500">
                      Current Reporter Pool Share: {revenueStats.current_reporter_pool_share_percent}%
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('admin_token');
                        const r = await fetch(apiUrl('/admin/revenue/settings'), { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
                        const j = await r.json();
                        if (j.success) {
                          setRateInput(String(j.data?.reporter_pool_share_percent ?? 20));
                          setRevenueStats((prev) => ({
                            ...prev,
                            current_reporter_pool_share_percent: j.data?.reporter_pool_share_percent ?? prev?.current_reporter_pool_share_percent,
                          }));
                        }
                      } catch (e) { /* ignore */ }
                      setShowRateModal(true);
                    }}
                    className="px-3 py-1 bg-blue-600 text-white rounded"
                  >
                    Adjust Reward Rate
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('admin_token');
                        const r = await fetch(apiUrl('/admin/revenue/settings'), { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
                        const j = await r.json();
                        if (j.success) {
                          setMinPayoutInput(String(j.data?.min_payout_amount ?? 50));
                        }
                      } catch (e) { /* ignore */ }
                      setShowMinPayoutModal(true);
                    }}
                    className="px-3 py-1 bg-indigo-600 text-white rounded"
                  >
                    Adjust Min Payout
                  </button>
                  {/* Manual calculate removed: server auto-creates current month */}
                </div>
              </div>

              {/* Monthly overview table */}
              {revenuePreview && (
                <div className="border rounded p-4 bg-blue-50">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-sm font-semibold text-blue-800">Current month preview (to date)</div>
                      <div className="text-xs text-blue-700">As of {new Date(revenuePreview.as_of).toLocaleString()}</div>
                    </div>
                    <button
                      onClick={loadRevenuePreview}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded"
                    >
                      Refresh
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm text-blue-900">
                    <div>Total turnover: ${money(revenuePreview.total_revenue)}</div>
                    <div>Reporter pool: ${money(revenuePreview.total_pool_amount)}</div>
                    <div>Total points: {revenuePreview.total_units}</div>
                    <div>Value per point: ${money(revenuePreview.amount_per_unit)}</div>
                  </div>
                </div>
              )}
              {revenuePreviewUsers && (
                <div className="border rounded p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Current month preview by user</div>
                      <div className="text-xs text-gray-500">As of {new Date(revenuePreviewUsers.as_of).toLocaleString()}</div>
                    </div>
                    <button
                      onClick={loadRevenuePreviewUsers}
                      className="px-2 py-1 text-xs bg-gray-800 text-white rounded"
                    >
                      Refresh
                    </button>
                  </div>
                  {revenuePreviewUsers.user_distributions?.length ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-3 py-2">User</th>
                            <th className="text-left px-3 py-2">Properties</th>
                            <th className="text-left px-3 py-2">Referrals</th>
                            <th className="text-left px-3 py-2">Total points</th>
                            <th className="text-left px-3 py-2">Pool amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenuePreviewUsers.user_distributions.map((row) => (
                            <tr key={`preview-${row.user_id}`} className="border-t">
                              <td className="px-3 py-2">
                                <div className="font-medium text-gray-900">{row.user?.name || `User ${row.user_id}`}</div>
                                <div className="text-xs text-gray-500">{row.user?.email || '—'}</div>
                              </td>
                              <td className="px-3 py-2">{row.property_points ?? 0}</td>
                              <td className="px-3 py-2">{row.referral_points ?? 0}</td>
                              <td className="px-3 py-2">{row.total_points ?? 0}</td>
                              <td className="px-3 py-2">${money(row.amount_earned)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No points recorded yet for the current month.</div>
                  )}
                </div>
              )}
              <div className="border rounded">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2">Month</th>
                        <th className="text-left px-3 py-2">Total Turnover</th>
                        <th className="text-left px-3 py-2">Amount rewards</th>
                        <th className="text-left px-3 py-2">Total points</th>
                        <th className="text-left px-3 py-2">Value per point</th>
                        <th className="text-right px-3 py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(distributions || []).map((d) => {
                        const isSelected = d.id === selectedDistributionId;
                        const monthName = new Date(d.year, (d.month || 1) - 1, 1).toLocaleString('default', { month: 'long' });
                        const totalTurnover = Number(d.total_platform_share || 0) + Number(d.total_pool_amount || 0);
                        return (
                          <tr key={d.id} className={`${isSelected ? 'bg-green-100' : ''} border-t cursor-pointer`} onClick={() => setSelectedDistributionId(d.id)}>
                            <td className="px-3 py-2 font-medium">{monthName}</td>
                            <td className="px-3 py-2">${money(totalTurnover)}</td>
                            <td className="px-3 py-2">${money(d.total_pool_amount)}</td>
                            <td className="px-3 py-2">{d.total_active_posts}</td>
                            <td className="px-3 py-2">${money(d.amount_per_post)}</td>
                            <td className="px-3 py-2 text-right">
                              <div className="inline-flex items-center space-x-2">
                                <button onClick={(e) => { e.stopPropagation(); setSelectedDistributionId(d.id); }} className={`px-2 py-1 rounded ${isSelected ? 'bg-green-700 text-white' : 'bg-blue-600 text-white'}`}>Detail</button>
                                {d.status === 'distributed' && (
                                  <button onClick={async (e) => {
                                    e.stopPropagation();
                                    const token = localStorage.getItem('admin_token');
                                    try {
                                      const r = await fetch(apiUrl(`/admin/revenue/reset-distribution/${d.id}`), {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
                                      });
                                      const j = await r.json();
                                      if (j.success) {
                                        toast.success('Distribution reset');
                                        await loadDistributions();
                                      } else {
                                        toast.error(j.message || 'Failed to reset');
                                      }
                                    } catch (e) {
                                      toast.error('Failed to reset');
                                    }
                                  }} className="px-2 py-1 bg-gray-700 text-white rounded">Reset</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {(!distributions || distributions.length === 0) && (
                        <tr>
                          <td colSpan="6" className="px-3 py-3 text-sm text-gray-500">No distributions yet</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Selected month detail */}
              {(() => {
                const selected = (distributions || []).find((x) => x.id === selectedDistributionId);
                if (!selected) return null;
                const monthName = new Date(selected.year, (selected.month || 1) - 1, 1).toLocaleString('default', { month: 'long' });

                const lifetimeByUser = new Map();
                (distributions || []).forEach((dist) => {
                  const uds = dist.user_distributions || dist.userDistributions || [];
                  uds.forEach((ud) => {
                    const current = lifetimeByUser.get(ud.user_id) || 0;
                    lifetimeByUser.set(ud.user_id, current + Number(ud.amount_earned || 0));
                  });
                });

                const uds = selected.user_distributions || selected.userDistributions || [];
                return (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{monthName}</h3>
                    <div className="border rounded overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-3 py-2">Username</th>
                            <th className="text-left px-3 py-2">Properties</th>
                            <th className="text-left px-3 py-2">Referrals</th>
                            <th className="text-left px-3 py-2">Total points</th>
                            <th className="text-left px-3 py-2">Rewards this month</th>
                            <th className="text-left px-3 py-2">Total rewards</th>
                            <th className="text-left px-3 py-2">Already received pay outs</th>
                            <th className="text-right px-3 py-2">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uds.map((ud) => (
                            <tr key={ud.id} className="border-t">
                              <td className="px-3 py-2">{ud.user?.name || ud.user_id}</td>
                              <td className="px-3 py-2">{ud.property_points ?? 0}</td>
                              <td className="px-3 py-2">{ud.referral_points ?? 0}</td>
                              <td className="px-3 py-2">{ud.active_posts_count ?? 0}</td>
                              <td className="px-3 py-2">${money(ud.amount_earned)}</td>
                              <td className="px-3 py-2 font-semibold">${money(lifetimeByUser.get(ud.user_id) || 0)}</td>
                              <td className="px-3 py-2">${money(ud.user?.earnings || 0)}</td>
                              <td className="px-3 py-2 text-right">
                                <div className="inline-flex items-center space-x-2">
                                  <button
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem('admin_token');
                                        const r = await fetch(apiUrl(`/admin/users/${ud.user_id}/properties`), {
                                          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
                                        });
                                        const j = await r.json();
                                        if (j.success) {
                                          const props = (j.data?.properties || []).filter((p) => {
                                            const d = new Date(p.created_at);
                                            return d.getFullYear() === Number(selected.year) && (d.getMonth() + 1) === Number(selected.month);
                                          });
                                          setDetailUser(j.data?.user || { id: ud.user_id, name: ud.user?.name });
                                          setDetailProps(props);
                                          setShowDetail(true);
                                        } else {
                                          toast.error(j.message || 'Failed to load properties');
                                        }
                                      } catch (e) {
                                        toast.error('Failed to load properties');
                                      }
                                    }}
                                    className="px-2 py-1 bg-blue-600 text-white rounded"
                                  >
                                    Detail
                                  </button>
                                {ud.status !== 'paid' && Number(ud.amount_earned) >= 50 && (
                                  <button
                                    onClick={async () => {
                                      const token = localStorage.getItem('admin_token');
                                      try {
                                        const r = await fetch(apiUrl(`/admin/revenue/user-distributions/${ud.id}/simulate-due`), {
                                          method: 'POST',
                                          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
                                        });
                                        const j = await r.json();
                                        if (j.success) {
                                          toast.success('Distributed for this user');
                                          await loadDistributions();
                                        } else {
                                          toast.error(j.message || 'Failed to distribute');
                                        }
                                      } catch (e) {
                                        toast.error('Failed to distribute');
                                      }
                                    }}
                                    className="px-2 py-1 bg-green-600 text-white rounded"
                                  >
                                    Distribute
                                  </button>
                                )}
                                {ud.status !== 'paid' && Number(ud.amount_earned) < 50 && (
                                  <span className="text-xs text-gray-500">Min $50 to distribute</span>
                                )}
                                  {ud.status === 'paid' && (
                                    <button
                                      onClick={() => {
                                        const selected = (distributions || []).find((x) => x.id === selectedDistributionId);
                                        const monthLabel = selected ? new Date(selected.year, (selected.month || 1) - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' }) : '';
                                        const defaultSubject = `SaleSign payout for ${monthLabel}`;
                                        const defaultBody = `
<p>Hello ${ud.user?.name || 'Reporter'},</p>
<p>We have processed your SaleSign payout for ${monthLabel}.</p>
<p>Amount: $${money(ud.amount_earned)}</p>
<p>Thank you for your contributions.</p>`;
                                        setManualPayload({
                                          ud,
                                          paypalEmail: ud.user?.paypal_email || ud.user?.email || '',
                                          subject: defaultSubject,
                                          amount: money(ud.amount_earned),
                                          body: defaultBody,
                                        });
                                        setShowManualPayout(true);
                                      }}
                                      className="px-2 py-1 bg-green-600 text-white rounded"
                                    >
                                      Advise payout
                                    </button>
                                  )}
                                  {ud.status === 'paid' && !ud.payout_refused_at && (
                                    <button
                                      onClick={async () => {
                                        const token = localStorage.getItem('admin_token');
                                        try {
                                          const r = await fetch(apiUrl(`/admin/revenue/user-distributions/${ud.id}/refuse-payout`), {
                                            method: 'POST',
                                            headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
                                          });
                                          const j = await r.json();
                                          if (j.success) {
                                            toast.success('Payout refused');
                                            await loadDistributions();
                                          } else {
                                            toast.error(j.message || 'Failed to refuse payout');
                                          }
                                        } catch (e) {
                                          toast.error('Failed to refuse payout');
                                        }
                                      }}
                                      className="px-2 py-1 bg-red-600 text-white rounded"
                                    >
                                      Refuse payout
                                    </button>
                                  )}
                                </div>
                                {ud.payout_advised_at && (
                                  <div className="text-xs text-green-700 mt-1">Advised: {new Date(ud.payout_advised_at).toLocaleString()}</div>
                                )}
                                {ud.payout_refused_at && (
                                  <div className="text-xs text-red-700 mt-1">Refused: {new Date(ud.payout_refused_at).toLocaleString()}</div>
                                )}
                              </td>
                            </tr>
                          ))}
                          {uds.length === 0 && (
                            <tr>
                              <td colSpan="6" className="px-3 py-3 text-sm text-gray-500">No users in this month</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Detail Modal */}
              {showDetail && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                  <div className="bg-white rounded shadow-lg max-w-2xl w-full">
                    <div className="p-4 border-b flex items-center justify-between">
                      <div>
                        <div className="text-lg font-semibold">Reported lands</div>
                        <div className="text-sm text-gray-600">{detailUser?.name} • {(() => {
                          const d = (distributions || []).find((x) => x.id === selectedDistributionId);
                          if (!d) return '';
                          return new Date(d.year, (d.month || 1) - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
                        })()}</div>
                      </div>
                      <button onClick={() => setShowDetail(false)} className="px-2 py-1 bg-gray-700 text-white rounded">Close</button>
                    </div>
                    <div className="p-4 max-h-[70vh] overflow-auto">
                      {detailProps.length === 0 ? (
                        <div className="text-sm text-gray-500">No reports in this month.</div>
                      ) : (
                        <div className="space-y-2">
                          {detailProps.map((p) => (
                            <div key={p.id} className="p-3 border rounded">
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="font-medium">{p.title || p.address || 'Property'}</div>
                                  <div className="text-xs text-gray-600">{p.address} {p.city ? `• ${p.city}` : ''}</div>
                                  <div className="text-xs text-gray-500">Reported at: {new Date(p.created_at).toLocaleString()}</div>
                                </div>
                                {(() => {
                                  const img = p.images?.[0] || null;
                                  const src = img ? getImageSrc(img) : null;
                                  return src ? (
                                    <PropertyImageThumbnail
                                      src={src}
                                      alt="thumb"
                                      className="w-16 h-16 rounded object-cover"
                                    />
                                  ) : null;
                                })()}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Manual Payout Modal */}
              {showManualPayout && manualPayload.ud && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                  <div className="bg-white rounded shadow-lg w-full max-w-2xl">
                    <div className="p-4 border-b flex items-center justify-between">
                      <div className="text-lg font-semibold">Manual payout</div>
                      <button onClick={() => setShowManualPayout(false)} className="px-2 py-1 bg-gray-700 text-white rounded">Close</button>
                    </div>
                    <div className="p-4 space-y-3 text-sm">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-600">PayPal email</label>
                          <div className="flex space-x-2">
                            <input value={manualPayload.paypalEmail} onChange={e => setManualPayload(p => ({...p, paypalEmail: e.target.value}))} className="px-2 py-1 border rounded w-full" />
                            <button onClick={() => navigator.clipboard.writeText(manualPayload.paypalEmail)} className="px-2 py-1 bg-gray-200 rounded">Copy</button>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">Subject</label>
                          <div className="flex space-x-2">
                            <input value={manualPayload.subject} onChange={e => setManualPayload(p => ({...p, subject: e.target.value}))} className="px-2 py-1 border rounded w-full" />
                            <button onClick={() => navigator.clipboard.writeText(manualPayload.subject)} className="px-2 py-1 bg-gray-200 rounded">Copy</button>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs text-gray-600">Amount to transfer (USD)</label>
                        <input type="number" step="0.01" min="0" value={manualPayload.amount} onChange={e => setManualPayload(p => ({...p, amount: e.target.value}))} className="px-2 py-1 border rounded w-40" />
                        <div className="text-xs text-gray-500 mt-1">Max: ${money(manualPayload.ud.amount_earned)}</div>
                      </div>

                      <div>
                        <label className="text-xs text-gray-600">Email content (HTML allowed)</label>
                        <textarea rows={8} value={manualPayload.body} onChange={e => setManualPayload(p => ({...p, body: e.target.value}))} className="w-full px-2 py-1 border rounded" />
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <button
                          onClick={async () => {
                            const token = localStorage.getItem('admin_token');
                            const r = await fetch(apiUrl(`/admin/revenue/user-distributions/${manualPayload.ud.id}/send-manual-email`), {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
                              body: JSON.stringify({ subject: manualPayload.subject, body: manualPayload.body })
                            });
                            const j = await r.json();
                            if (j.success) toast.success('Email sent'); else toast.error(j.message || 'Failed to send email');
                          }}
                          className="px-3 py-1 bg-blue-600 text-white rounded"
                        >
                          Send email
                        </button>

                        <button
                          onClick={async () => {
                            const token = localStorage.getItem('admin_token');
                            const amount = Number(manualPayload.amount);
                            const r = await fetch(apiUrl(`/admin/revenue/user-distributions/${manualPayload.ud.id}/close-manual-payout`), {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
                              body: JSON.stringify({ amount, reference: manualPayload.subject, notes: 'Manual payout recorded by admin' })
                            });
                            const j = await r.json();
                            if (j.success) {
                              toast.success('Payout marked as paid and balances updated');
                              setShowManualPayout(false);
                              await loadDistributions();
                            } else {
                              toast.error(j.message || 'Failed to close payout');
                            }
                          }}
                          className="px-3 py-1 bg-green-600 text-white rounded"
                        >
                          Mark as paid
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Reward Rate Modal */}
              {showRateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                  <div className="bg-white rounded shadow-lg w-full max-w-md">
                    <div className="p-4 border-b flex items-center justify-between">
                      <div className="text-lg font-semibold">Adjust Reward Rate</div>
                      <button onClick={() => setShowRateModal(false)} className="px-2 py-1 bg-gray-700 text-white rounded">Close</button>
                    </div>
                    <div className="p-4 space-y-3 text-sm">
                      <div className="text-gray-700">Set reporter pool share percentage. Default is 20%.</div>
                      <div className="flex items-center space-x-2">
                        <input type="number" min="0" max="100" step="0.1" value={rateInput} onChange={(e) => setRateInput(e.target.value)} className="px-2 py-1 border rounded w-28" />
                        <span>% to reporters</span>
                      </div>
                      <div className="text-xs text-gray-500">Platform share will be {Math.max(0, 100 - Number(rateInput || 0)).toFixed(1)}%.</div>
                      {revenueStats?.current_reporter_pool_share_percent !== undefined && (
                        <div className="text-xs text-blue-600 font-medium">
                          Current rate: {revenueStats.current_reporter_pool_share_percent}%
                        </div>
                      )}
                      <div className="pt-2">
                        <button
                          onClick={async () => {
                            const token = localStorage.getItem('admin_token');
                            const payload = { reporter_pool_share_percent: Number(rateInput) };
                            const doPost = async () => {
                              const r = await fetch(apiUrl('/admin/revenue/settings'), {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
                                body: JSON.stringify(payload)
                              });
                              return await r.json();
                            };
                            try {
                              let j;
                              try {
                                j = await doPost();
                              } catch (e1) {
                                // Retry once after a short delay to handle server restart blip
                                await new Promise(res => setTimeout(res, 1000));
                                j = await doPost();
                              }
                              if (j.success) {
                                toast.success(`Reward rate updated to ${j.data?.reporter_pool_share_percent ?? rateInput}%`);
                                if (j.data && typeof j.data.reporter_pool_share_percent !== 'undefined') {
                                  setRateInput(String(j.data.reporter_pool_share_percent));
                                  setRevenueStats((prev) => ({
                                    ...prev,
                                    current_reporter_pool_share_percent: j.data.reporter_pool_share_percent,
                                  }));
                                }
                                // Allow backend to finish deferred reload after settings write
                                await new Promise(res => setTimeout(res, 1500));
                                await loadRevenueStats();
                                await loadDistributions();
                                setShowRateModal(false);
                              } else {
                                toast.error(j.message || 'Failed to update rate');
                              }
                            } catch (e) {
                              toast.error('Failed to update rate');
                            }
                          }}
                          className="px-3 py-1 bg-blue-600 text-white rounded"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Min Payout Modal */}
              {showMinPayoutModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                  <div className="bg-white rounded shadow-lg w-full max-w-md">
                    <div className="p-4 border-b flex items-center justify-between">
                      <div className="text-lg font-semibold">Adjust Minimum Payout</div>
                      <button onClick={() => setShowMinPayoutModal(false)} className="px-2 py-1 bg-gray-700 text-white rounded">Close</button>
                    </div>
                    <div className="p-4 space-y-3 text-sm">
                      <div className="text-gray-700">Set the minimum pending balance required before a payout can be processed.</div>
                      <div className="flex items-center space-x-2">
                        <span>$</span>
                        <input type="number" min="0" step="0.01" value={minPayoutInput} onChange={(e) => setMinPayoutInput(e.target.value)} className="px-2 py-1 border rounded w-32" />
                      </div>
                      <div className="pt-2">
                        <button
                          onClick={async () => {
                            const token = localStorage.getItem('admin_token');
                            const payload = { min_payout_amount: Number(minPayoutInput) };
                            const doPost = async () => {
                              const r = await fetch(apiUrl('/admin/revenue/settings'), {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
                                body: JSON.stringify(payload)
                              });
                              return await r.json();
                            };
                            try {
                              let j;
                              try {
                                j = await doPost();
                              } catch (e1) {
                                await new Promise(res => setTimeout(res, 1000));
                                j = await doPost();
                              }
                              if (j.success) {
                                toast.success('Minimum payout updated');
                                if (j.data && typeof j.data.min_payout_amount !== 'undefined') {
                                  setMinPayoutInput(String(j.data.min_payout_amount));
                                }
                                // Allow backend to finish deferred reload after settings write
                                await new Promise(res => setTimeout(res, 1500));
                                await loadRevenueStats();
                                await loadDistributions();
                                setShowMinPayoutModal(false);
                              } else {
                                toast.error(j.message || 'Failed to update minimum payout');
                              }
                            } catch (e) {
                              toast.error('Failed to update minimum payout');
                            }
                          }}
                          className="px-3 py-1 bg-indigo-600 text-white rounded"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {activeTab === 'users' && (
          <AdminUserManagement />
        )}

        {activeTab === 'searches' && (
          <div className="space-y-6">
            {areaSearchesLoading && (
              <div className="text-sm text-gray-500">Loading area searches…</div>
            )}
            {areaSearchesError && (
              <div className="text-sm text-red-600">{areaSearchesError}</div>
            )}
            {!areaSearchesLoading && !areaSearchesError && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 bg-white shadow rounded-lg overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">All searches on map</h3>
                      <p className="text-sm text-gray-500">{areaSearches.searches.length} area alert(s)</p>
                    </div>
                    <div className="relative" style={{ height: '420px' }}>
                      <MapContainer
                        center={[20, 0]}
                        zoom={2}
                        style={{ height: '100%', width: '100%' }}
                        scrollWheelZoom
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {areaSearches.searches.filter((s) => s.bounds && s.bounds.length === 2).map((s) => (
                          <Rectangle
                            key={s.id}
                            bounds={[s.bounds[0], s.bounds[1]]}
                            pathOptions={{
                              color: '#2563eb',
                              fillColor: '#3b82f6',
                              fillOpacity: 0.2,
                              weight: 2,
                            }}
                            eventHandlers={{
                              click: () => {},
                            }}
                          >
                            <Popup>
                              <div className="text-sm">
                                <div className="font-medium">{s.label || 'Area'}</div>
                                <div className="text-gray-600">{s.user?.email || '—'}</div>
                                {s.notify_on_new_properties && <span className="text-green-600">New property alerts on</span>}
                              </div>
                            </Popup>
                          </Rectangle>
                        ))}
                        <FitBoundsToSearches searches={areaSearches.searches} />
                      </MapContainer>
                    </div>
                  </div>
                  <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">Top 10 areas</h3>
                      <p className="text-sm text-gray-500">Most searched (country / region)</p>
                    </div>
                    <div className="p-4">
                      <ol className="list-decimal list-inside space-y-2">
                        {areaSearches.top_areas.map((area, idx) => (
                          <li key={idx} className="text-sm flex justify-between gap-2">
                            <span className="truncate">{area.label || `${area.region || ''} ${area.country || ''}`.trim() || '—'}</span>
                            <span className="font-medium text-blue-600 whitespace-nowrap">{area.search_count}</span>
                          </li>
                        ))}
                        {areaSearches.top_areas.length === 0 && (
                          <li className="text-gray-500">No data yet</li>
                        )}
                      </ol>
                    </div>
                  </div>
                </div>
                <div className="bg-white shadow rounded-lg overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Who is searching where</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Area</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">New alerts</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {areaSearches.searches.map((s) => (
                          <tr key={s.id}>
                            <td className="px-4 py-2 text-sm">
                              <div>{s.user?.email || '—'}</div>
                              {s.user?.name && <div className="text-gray-500">{s.user.name}</div>}
                            </td>
                            <td className="px-4 py-2 text-sm">{s.label || '—'}</td>
                            <td className="px-4 py-2 text-sm">{s.notify_on_new_properties ? 'Yes' : 'No'}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</td>
                          </tr>
                        ))}
                        {areaSearches.searches.length === 0 && (
                          <tr>
                            <td colSpan={4} className="px-4 py-4 text-center text-gray-500 text-sm">No area searches yet</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'background' && (
          <BackgroundImageManager />
        )}

        {activeTab === 'terms' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Terms of Service</h3>
                  <p className="text-sm text-gray-500">Edit the terms shown during signup.</p>
                </div>
                <button
                  type="button"
                  onClick={saveTermsSettings}
                  disabled={termsSaving}
                  className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {termsSaving ? 'Saving…' : 'Save terms'}
                </button>
              </div>

              {termsLoading ? (
                <div className="text-sm text-gray-500">Loading terms…</div>
              ) : termsError ? (
                <div className="text-sm text-red-600">{termsError}</div>
              ) : (
                <div className="grid gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Land seeker terms</label>
                    <textarea
                      value={termsInput.land_seeker}
                      onChange={(event) => setTermsInput((prev) => ({ ...prev, land_seeker: event.target.value }))}
                      rows={10}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Land reporter terms</label>
                    <textarea
                      value={termsInput.land_reporter}
                      onChange={(event) => setTermsInput((prev) => ({ ...prev, land_reporter: event.target.value }))}
                      rows={10}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              )}

              {termsSettings?.updated_at && (
                <div className="text-xs text-gray-500">
                  Last updated: {new Date(termsSettings.updated_at).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'invoice' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Invoice Settings</h3>
                  <p className="text-sm text-gray-500">Update the company details shown on subscription invoices.</p>
                </div>
                <button
                  type="button"
                  onClick={saveInvoiceSettings}
                  disabled={invoiceSaving}
                  className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {invoiceSaving ? 'Saving…' : 'Save settings'}
                </button>
              </div>

              {invoiceLoading ? (
                <div className="text-sm text-gray-500">Loading invoice settings…</div>
              ) : invoiceError ? (
                <div className="text-sm text-red-600">{invoiceError}</div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company name</label>
                    <input
                      type="text"
                      value={invoiceInput.company_name}
                      onChange={(event) => setInvoiceInput((prev) => ({ ...prev, company_name: event.target.value }))}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={invoiceInput.email}
                      onChange={(event) => setInvoiceInput((prev) => ({ ...prev, email: event.target.value }))}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address line 1</label>
                    <input
                      type="text"
                      value={invoiceInput.address_line1}
                      onChange={(event) => setInvoiceInput((prev) => ({ ...prev, address_line1: event.target.value }))}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address line 2</label>
                    <input
                      type="text"
                      value={invoiceInput.address_line2}
                      onChange={(event) => setInvoiceInput((prev) => ({ ...prev, address_line2: event.target.value }))}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                    <input
                      type="text"
                      value={invoiceInput.zip}
                      onChange={(event) => setInvoiceInput((prev) => ({ ...prev, zip: event.target.value }))}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={invoiceInput.city}
                      onChange={(event) => setInvoiceInput((prev) => ({ ...prev, city: event.target.value }))}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State / Province</label>
                    <input
                      type="text"
                      value={invoiceInput.state}
                      onChange={(event) => setInvoiceInput((prev) => ({ ...prev, state: event.target.value }))}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <input
                      type="text"
                      value={invoiceInput.country}
                      onChange={(event) => setInvoiceInput((prev) => ({ ...prev, country: event.target.value }))}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID</label>
                    <input
                      type="text"
                      value={invoiceInput.tax_id}
                      onChange={(event) => setInvoiceInput((prev) => ({ ...prev, tax_id: event.target.value }))}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              )}

              {invoiceSettings?.updated_at && (
                <div className="text-xs text-gray-500">
                  Last updated: {new Date(invoiceSettings.updated_at).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'admins' && admin?.role === 'super_admin' && (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg">
              <div className="p-6 border-b flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Admin Accounts</h2>
                  <p className="text-sm text-gray-600">Manage manager/admin + moderator credentials</p>
                </div>
                <button
                  type="button"
                  onClick={loadAdminAccounts}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm"
                  disabled={adminsLoading}
                >
                  {adminsLoading ? 'Loading…' : 'Refresh'}
                </button>
              </div>
              <div className="p-6">
                {adminsError && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded text-sm">{adminsError}</div>
                )}

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-600 border-b">
                        <th className="py-2 pr-4">Name</th>
                        <th className="py-2 pr-4">Email</th>
                        <th className="py-2 pr-4">Role</th>
                        <th className="py-2 pr-4">Active</th>
                        <th className="py-2 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(adminAccounts || []).map((a) => (
                        <tr key={a.id} className="border-b">
                          <td className="py-2 pr-4">{a.name}</td>
                          <td className="py-2 pr-4">{a.email}</td>
                          <td className="py-2 pr-4">{a.role}</td>
                          <td className="py-2 pr-4">{a.is_active ? 'Yes' : 'No'}</td>
                          <td className="py-2 pr-4">
                            <button
                              type="button"
                              onClick={() => openEditAdmin(a)}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                      {(!adminsLoading && (adminAccounts || []).length === 0) && (
                        <tr>
                          <td colSpan={5} className="py-4 text-gray-500">No admin accounts found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {editingAdmin && (
              <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                  <div className="p-4 border-b flex items-center justify-between">
                    <div className="text-lg font-semibold">Edit Admin</div>
                    <button
                      type="button"
                      onClick={() => setEditingAdmin(null)}
                      className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                    >
                      Close
                    </button>
                  </div>
                  <div className="p-4 space-y-3 text-sm">
                    <div>
                      <div className="text-gray-600 mb-1">Name</div>
                      <input
                        className="w-full px-3 py-2 border rounded"
                        value={adminEditForm.name}
                        onChange={(e) => setAdminEditForm((p) => ({ ...p, name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <div className="text-gray-600 mb-1">Email</div>
                      <input
                        className="w-full px-3 py-2 border rounded"
                        value={adminEditForm.email}
                        onChange={(e) => setAdminEditForm((p) => ({ ...p, email: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-gray-600 mb-1">Role</div>
                        <select
                          className="w-full px-3 py-2 border rounded"
                          value={adminEditForm.role}
                          onChange={(e) => setAdminEditForm((p) => ({ ...p, role: e.target.value }))}
                        >
                          <option value="admin">Admin (Manager)</option>
                          <option value="moderator">Moderator</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      </div>
                      <div>
                        <div className="text-gray-600 mb-1">Active</div>
                        <select
                          className="w-full px-3 py-2 border rounded"
                          value={adminEditForm.is_active ? '1' : '0'}
                          onChange={(e) => setAdminEditForm((p) => ({ ...p, is_active: e.target.value === '1' }))}
                        >
                          <option value="1">Yes</option>
                          <option value="0">No</option>
                        </select>
                      </div>
                    </div>

                    <div className="pt-2 border-t">
                      <div className="text-gray-700 font-medium mb-2">Set new password (optional)</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-gray-600 mb-1">Password</div>
                          <input
                            type="password"
                            className="w-full px-3 py-2 border rounded"
                            value={adminEditForm.password}
                            onChange={(e) => setAdminEditForm((p) => ({ ...p, password: e.target.value }))}
                          />
                        </div>
                        <div>
                          <div className="text-gray-600 mb-1">Confirm</div>
                          <input
                            type="password"
                            className="w-full px-3 py-2 border rounded"
                            value={adminEditForm.password_confirmation}
                            onChange={(e) => setAdminEditForm((p) => ({ ...p, password_confirmation: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Leave blank to keep existing password.</div>
                    </div>
                  </div>
                  <div className="p-4 border-t flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setEditingAdmin(null)}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveAdminEdits}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

const buildPropertyEditData = (property = {}) => {
  const inferredCategory = (() => {
    const category = property.property_category || property.propertyCategory;
    if (category) return category;
    if (property.property_type && property.property_type.toLowerCase() === 'rent') {
      return property.property_category || 'apartment';
    }
    return 'land_lot';
  })();

  const lotSizeValue = property.lot_size_value ?? property.size ?? '';
  // Map backend canonical units to dropdown option values (hectare→hectares, acre→acres)
  const rawUnit = property.lot_size_unit ?? property.size_unit ?? 'sq_m';
  const lotSizeUnit = (() => {
    const text = String(rawUnit || '').trim().toLowerCase();
    if (text.includes('hect') || text === 'ha') return 'hectares';
    if (text.includes('acre')) return 'acres';
    if (text.includes('ft')) return 'sq_ft';
    if (text === 'sqm' || text === 'm2' || text === 'sq_m') return 'sq_m';
    return 'sq_m';
  })();
  const saleTypeDetail = property.sale_type_detail
    ?? property.sale_type
    ?? (property.property_type === 'rent' ? 'for_rent' : 'for_sale');

  const normalizeBooleanSelect = (value) => {
    if (value === true || value === 1 || value === '1' || value === 'true') return 'yes';
    if (value === false || value === 0 || value === '0' || value === 'false') return 'no';
    if (value === 'yes' || value === 'no') return value;
    return '';
  };

  return {
    client_reference: property.client_reference
      ?? property.unique_property_id
      ?? property.reference_code
      ?? '',
    title: property.title || '',
    description: property.description || '',
    property_category: inferredCategory,
    sale_type_detail: saleTypeDetail,
    property_type: property.property_type || (saleTypeDetail === 'for_rent' ? 'rent' : 'sale'),
    is_owner: normalizeBooleanSelect(property.is_owner),
    has_title: normalizeBooleanSelect(property.has_title),
    contact_name: property.contact_name || '',
    phone: property.phone || '',
    whatsapp: property.whatsapp || '',
    email: property.email || '',
    address: property.address || '',
    city: property.city || '',
    region: property.region || '',
    country: property.country || '',
    lot_size_value: lotSizeValue,
    lot_size_unit: lotSizeUnit,
    price: property.price ?? '',
    currency: property.currency || 'USD',
    living_area_value: property.living_area_value ?? '',
    living_area_unit: property.living_area_unit || 'sqm',
    bedrooms: property.bedrooms ?? '',
    bathrooms: property.bathrooms ?? '',
    year_built: property.year_built ?? '',
    latitude: property.latitude || '',
    longitude: property.longitude || '',
    gps_latitude: property.gps_latitude || '',
    gps_longitude: property.gps_longitude || '',
    manual_latitude: property.manual_latitude || '',
    manual_longitude: property.manual_longitude || '',
    location_source: property.location_source || 'gps',
  };
};

// Property Card Component
const PropertyCard = ({
  property,
  onApprove,
  onReject,
  onUpdate,
  onDelete,
  onDeleteMedia,
  onUploadMedia,
  onCreateVerificationLink,
  verificationLink,
  isCreatingVerificationLink,
  getImageSrc,
  autoExpand = false,
}) => {
  const { t, i18n } = useTranslation();
  const [whatsappLanguage, setWhatsappLanguage] = useState('en');
  const [showActions, setShowActions] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragActive, setIsDragActive] = useState(false);
  // Commission controls removed
  const [rejectionReason, setRejectionReason] = useState('');
  const [editData, setEditData] = useState(buildPropertyEditData(property));
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);
  const [geocodeLabel, setGeocodeLabel] = useState(property.address ? [property.address, property.city, property.region, property.country].filter(Boolean).join(', ') : '');
  const sanitizeText = (value, maxLength) => {
    if (value === null || value === undefined) return '';
    const text = String(value).trim();
    return maxLength ? text.slice(0, maxLength) : text;
  };
  const cardRef = useRef(null);
  const uploadInputRef = useRef(null);

  useEffect(() => {
    setEditData(buildPropertyEditData(property));
    setGeocodeLabel(property.address ? [property.address, property.city, property.region, property.country].filter(Boolean).join(', ') : '');
  }, [property]);

  useEffect(() => {
    if (autoExpand) {
      setShowEdit(true);
      if (property.status === 'pending') {
        setShowActions(true);
      }
      requestAnimationFrame(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [autoExpand, property.status]);

  const handleApprove = () => {
    onApprove(property.id);
    setShowActions(false);
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    onReject(property.id, rejectionReason);
    setShowActions(false);
    setRejectionReason('');
  };

  const handleSave = () => {
    const isResidential = ['apartment', 'house'].includes(editData.property_category);
    const normalizedLotUnit =
      editData.lot_size_unit === 'acres'
        ? 'acre'
        : (editData.lot_size_unit === 'hectares' ? 'hectare' : editData.lot_size_unit);
    const normalizeBooleanPayload = (value) => {
      if (value === 'yes' || value === true || value === 1 || value === '1' || value === 'true') return 'yes';
      if (value === 'no' || value === false || value === 0 || value === '0' || value === 'false') return 'no';
      return '';
    };
    const payload = {
      client_reference: editData.client_reference,
      title: editData.title,
      description: editData.description,
      property_category: editData.property_category,
      sale_type_detail: editData.sale_type_detail,
      property_type: editData.property_type || (editData.sale_type_detail === 'for_rent' ? 'rent' : 'sale'),
      is_owner: normalizeBooleanPayload(editData.is_owner),
      has_title: normalizeBooleanPayload(editData.has_title),
      contact_name: editData.contact_name,
      phone: editData.phone,
      whatsapp: editData.whatsapp,
      email: editData.email,
      address: editData.address,
      city: editData.city,
      region: editData.region,
      country: editData.country,
      // Send both alias + canonical fields for backwards compatibility
      lot_size_value: editData.lot_size_value,
      lot_size_unit: editData.lot_size_unit,
      size: editData.lot_size_value,
      size_unit: normalizedLotUnit,
      price: editData.price,
      currency: editData.currency,
      living_area_value: isResidential ? editData.living_area_value : '',
      living_area_unit: isResidential ? editData.living_area_unit : '',
      bedrooms: isResidential ? editData.bedrooms : '',
      bathrooms: isResidential ? editData.bathrooms : '',
      year_built: isResidential ? editData.year_built : '',
      latitude: editData.latitude,
      longitude: editData.longitude,
    location_source: editData.location_source,
    };

    onUpdate(property.id, payload);
    setShowEdit(false);
  };

  const handleDelete = () => {
    onDelete(property.id, property.title);
  };

  const handleDeleteMedia = (media) => {
    if (onDeleteMedia) {
      onDeleteMedia(property.id, media);
    }
  };

  const handleUploadMediaClick = () => {
    uploadInputRef.current?.click();
  };

  const handleUploadMediaFiles = async (files) => {
    if (!files.length || !onUploadMedia) return;
    setIsUploading(true);
    setUploadProgress(0);
    await onUploadMedia(property.id, files, setUploadProgress);
    setIsUploading(false);
    setUploadProgress(0);
  };

  const handleUploadMediaChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      event.target.value = '';
      return;
    }
    await handleUploadMediaFiles(files);
    event.target.value = '';
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = () => {
    setIsDragActive(false);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragActive(false);
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;
    await handleUploadMediaFiles(files);
  };

  const resetEditState = () => {
    setEditData(buildPropertyEditData(property));
    setGeocodeLabel(property.address ? [property.address, property.city, property.region, property.country].filter(Boolean).join(', ') : '');
  };

  const handleStartEdit = () => {
    resetEditState();
    setShowEdit(true);
  };

  const handleCancelEdit = () => {
    resetEditState();
    setShowEdit(false);
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === 'sale_type_detail') {
        next.property_type = value === 'for_rent' ? 'rent' : 'sale';
      }

      if (field === 'property_category' && !['apartment', 'house'].includes(value)) {
        next.living_area_value = '';
        next.bedrooms = '';
        next.bathrooms = '';
        next.year_built = '';
      }

      return next;
    });
  };

  const handleUseRecordedLocation = (source) => {
    if (source === 'manual') {
      if (!editData.manual_latitude || !editData.manual_longitude) {
        toast.error('No manual location recorded for this property');
        return;
      }
      setEditData((prev) => ({
        ...prev,
        latitude: prev.manual_latitude,
        longitude: prev.manual_longitude,
        location_source: 'manual',
      }));
      toast.success('Using manually selected location as the public position');
      return;
    }

    if (!editData.gps_latitude || !editData.gps_longitude) {
      toast.error('No GPS location recorded for this property');
      return;
    }

    setEditData((prev) => ({
      ...prev,
      latitude: prev.gps_latitude,
      longitude: prev.gps_longitude,
      location_source: 'gps',
    }));
    toast.success('Using GPS captured location as the public position');
  };

  const isResidentialCategory = ['apartment', 'house'].includes(editData.property_category);

  return (
    <div id={`property-card-${property.id}`} ref={cardRef} className="border rounded-lg p-4">
      {/* Header with images and basic info */}
      <div className="flex items-start space-x-4 mb-4">
        <div className="flex-shrink-0">
          {(() => {
            const primaryImage =
              property.images?.find((img) => img?.is_primary && img?.file_type !== 'video') ||
              property.images?.find((img) => img?.file_type !== 'video') ||
              property.images?.[0] ||
              null;
            if (!primaryImage) {
              return (
                <div className="w-20 h-20 rounded-lg bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
                  No Media
                </div>
              );
            }
            const primaryMediaSrc = getImageSrc ? getImageSrc(primaryImage) : null;
            if (!primaryMediaSrc) {
              return (
                <div className="w-20 h-20 rounded-lg bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
                  No Media
                </div>
              );
            }
            const isVideo = primaryImage.file_type === 'video';
            if (isVideo) {
              return (
                <video
                  src={primaryMediaSrc}
                  className="w-20 h-20 rounded-lg object-cover cursor-pointer"
                  onClick={() => setShowImages(!showImages)}
                  muted
                  loop
                />
              );
            }
            return (
              <PropertyImageThumbnail
                src={primaryMediaSrc}
                alt={property.title}
                className="w-20 h-20 rounded-lg object-cover cursor-pointer"
                onClick={() => setShowImages(!showImages)}
              />
            );
          })()}
          {property.images?.length > 1 && (
            <div className="text-xs text-gray-500 mt-1 text-center">
              +{property.images.length - 1} more
            </div>
          )}
        </div>
        
        <div className="flex-1">
          {showEdit ? (
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
                  <input
                    type="text"
                    value={editData.title}
                    onChange={(e) => handleEditChange('title', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                    placeholder="Property title"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Unique property ID</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 px-3 py-2 border border-gray-200 rounded bg-gray-50 text-sm text-gray-700 font-mono">
                      {editData.client_reference || 'Not assigned'}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (!editData.client_reference) return;
                        navigator.clipboard?.writeText(editData.client_reference).then(
                          () => toast.success('Unique ID copied to clipboard'),
                          () => toast.error('Failed to copy unique ID')
                        );
                      }}
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        editData.client_reference ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                      disabled={!editData.client_reference}
                    >
                      Copy
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Assigned automatically when reported. This value cannot be edited.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Contact name</label>
                  <input
                    type="text"
                    value={editData.contact_name}
                    onChange={(e) => handleEditChange('contact_name', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                    placeholder="Owner or agent name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description *</label>
                <textarea
                  value={editData.description}
                  onChange={(e) => handleEditChange('description', e.target.value)}
                  className="w-full px-2 py-1 text-sm border rounded"
                  rows={3}
                  placeholder="Main marketing copy for this listing"
                />
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Property type *</label>
                  <select
                    value={editData.property_category}
                    onChange={(e) => handleEditChange('property_category', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                  >
                    <option value="land_lot">Land / Lot</option>
                    <option value="apartment">Apartment</option>
                    <option value="house">House</option>
                    <option value="commercial">Commercial</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Sale type *</label>
                  <select
                    value={editData.sale_type_detail}
                    onChange={(e) => handleEditChange('sale_type_detail', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                  >
                    <option value="for_sale">For sale</option>
                    <option value="for_sale_by_owner">For sale by owner</option>
                    <option value="for_rent">For rent</option>
                    <option value="for_lease">For lease</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('reportPage.form.isOwner')} *</label>
                  <select
                    value={editData.is_owner}
                    onChange={(e) => handleEditChange('is_owner', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="yes">{t('common.yes')}</option>
                    <option value="no">{t('common.no')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('reportPage.form.titleDeed')} *</label>
                  <select
                    value={editData.has_title}
                    onChange={(e) => handleEditChange('has_title', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="yes">{t('common.yes')}</option>
                    <option value="no">{t('common.no')}</option>
                    <option value="not_sure">{t('reportPage.form.notSure')}</option>
                  </select>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone number *</label>
                  <input
                    type="tel"
                    value={editData.phone}
                    onChange={(e) => handleEditChange('phone', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                    placeholder="+507 6000 0000"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">WhatsApp</label>
                  <input
                    type="tel"
                    value={editData.whatsapp}
                    onChange={(e) => handleEditChange('whatsapp', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                    placeholder="+507 6000 0000"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">E-mail</label>
                  <input
                    type="email"
                    value={editData.email}
                    onChange={(e) => handleEditChange('email', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                    placeholder="contact@example.com"
                  />
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Size of lot</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={editData.lot_size_value}
                      onChange={(e) => handleEditChange('lot_size_value', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded"
                      placeholder="e.g. 500"
                    />
                    <select
                      value={editData.lot_size_unit}
                      onChange={(e) => handleEditChange('lot_size_unit', e.target.value)}
                      className="px-2 py-1 text-sm border rounded bg-gray-50"
                    >
                      <option value="sq_m">m²</option>
                      <option value="sq_ft">ft²</option>
                      <option value="hectares">Hectares</option>
                      <option value="acres">Acres</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Price</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={editData.price}
                      onChange={(e) => handleEditChange('price', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded"
                      placeholder="e.g. 250000"
                    />
                    <select
                      value={editData.currency}
                      onChange={(e) => handleEditChange('currency', e.target.value)}
                      className="px-2 py-1 text-sm border rounded bg-gray-50"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="PAB">PAB</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>
              </div>

              {isResidentialCategory && (
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Size of living area</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={editData.living_area_value}
                        onChange={(e) => handleEditChange('living_area_value', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded"
                        placeholder="e.g. 120"
                      />
                      <select
                        value={editData.living_area_unit}
                        onChange={(e) => handleEditChange('living_area_unit', e.target.value)}
                        className="px-2 py-1 text-sm border rounded bg-gray-50"
                      >
                        <option value="sqm">m²</option>
                        <option value="sq_ft">ft²</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Number of bedrooms</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={editData.bedrooms}
                      onChange={(e) => handleEditChange('bedrooms', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded"
                      placeholder="e.g. 3"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Number of bathrooms</label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={editData.bathrooms}
                      onChange={(e) => handleEditChange('bathrooms', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded"
                      placeholder="e.g. 2"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Year built</label>
                    <input
                      type="number"
                      min="1800"
                      max={new Date().getFullYear()}
                      value={editData.year_built}
                      onChange={(e) => handleEditChange('year_built', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded"
                      placeholder="e.g. 2018"
                    />
                  </div>
                </div>
              )}

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                  <input
                    type="text"
                    value={editData.address}
                    onChange={(e) => handleEditChange('address', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                    placeholder="Street and number"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                  <input
                    type="text"
                    value={editData.city}
                    onChange={(e) => handleEditChange('city', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Region / Province</label>
                  <input
                    type="text"
                    value={editData.region}
                    onChange={(e) => handleEditChange('region', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                    placeholder="Region or province"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Country</label>
                  <input
                    type="text"
                    value={editData.country}
                    onChange={(e) => handleEditChange('country', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                    placeholder="Country"
                  />
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Latitude (Primary)</label>
                  <input
                    type="number"
                    step="any"
                    value={editData.latitude}
                    onChange={(e) => handleEditChange('latitude', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                    placeholder="Latitude"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Longitude (Primary)</label>
                  <input
                    type="number"
                    step="any"
                    value={editData.longitude}
                    onChange={(e) => handleEditChange('longitude', e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded"
                    placeholder="Longitude"
                  />
                </div>
              </div>

              {/* GPS and User Location Display */}
              {(editData.gps_latitude || editData.manual_latitude) && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <h5 className="text-xs font-semibold text-gray-700 mb-2">📍 Recorded Locations (for fraud detection)</h5>
                  <div className="space-y-2 text-xs">
                    {editData.gps_latitude && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 font-medium">GPS Location:</span>
                        <span className="text-gray-800 font-mono">
                          {parseFloat(editData.gps_latitude).toFixed(6)}, {parseFloat(editData.gps_longitude).toFixed(6)}
                        </span>
                      </div>
                    )}
                    {editData.manual_latitude && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 font-medium">User Location (Manual):</span>
                        <span className="text-gray-800 font-mono">
                          {parseFloat(editData.manual_latitude).toFixed(6)}, {parseFloat(editData.manual_longitude).toFixed(6)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-300">
                      <span className="text-gray-600 font-medium">Location Source:</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        editData.location_source === 'manual' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {editData.location_source === 'manual' ? 'User Selected' : 'GPS'}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleUseRecordedLocation('gps')}
                        disabled={!editData.gps_latitude || !editData.gps_longitude}
                        className={`px-2 py-1 rounded text-xs font-semibold border transition ${
                          editData.location_source === 'gps'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'
                        } ${(!editData.gps_latitude || !editData.gps_longitude) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Publish GPS location
                      </button>
                      <button
                        type="button"
                        onClick={() => handleUseRecordedLocation('manual')}
                        disabled={!editData.manual_latitude || !editData.manual_longitude}
                        className={`px-2 py-1 rounded text-xs font-semibold border transition ${
                          editData.location_source === 'manual'
                            ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-white text-amber-600 border-amber-300 hover:bg-amber-50'
                        } ${(!editData.manual_latitude || !editData.manual_longitude) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Publish manual location
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-2 text-xs text-gray-600">
                <button
                  type="button"
                  onClick={async () => {
                    if (!editData.latitude || !editData.longitude) {
                      toast.error('Set latitude and longitude first');
                      return;
                    }
                    setIsFetchingAddress(true);
                    try {
                      const res = await fetch(`${apiUrl('/geocode/reverse')}?lat=${encodeURIComponent(editData.latitude)}&lng=${encodeURIComponent(editData.longitude)}`);
                      const json = await res.json();
                      if (res.ok && json?.success && json.data) {
                        const { street, township, district, province, country, label } = json.data;
                        const safeStreet = sanitizeText(street, 255);
                        const inferredCity = sanitizeText(township || district || '', 100);
                        const safeProvince = sanitizeText(province, 100);
                        const safeCountry = sanitizeText(country, 100);

                        if (safeStreet) {
                          handleEditChange('address', safeStreet);
                        }
                        if (inferredCity) {
                          handleEditChange('city', inferredCity);
                        }
                        if (safeProvince) {
                          handleEditChange('region', safeProvince);
                        }
                        if (safeCountry) {
                          handleEditChange('country', safeCountry);
                        }
                        const fallbackLabel = [safeStreet, inferredCity, safeProvince, safeCountry].filter(Boolean).join(', ');
                        setGeocodeLabel(sanitizeText(label, 255) || fallbackLabel);
                        toast.success('Address fields updated from coordinates');
                      } else {
                        toast.error(json?.message || 'Could not resolve address');
                      }
                    } catch (err) {
                      console.error('Error fetching address from coordinates:', err);
                      toast.error('Failed to fetch address');
                    } finally {
                      setIsFetchingAddress(false);
                    }
                  }}
                  className="px-2 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
                  disabled={isFetchingAddress}
                >
                  {isFetchingAddress ? 'Fetching…' : 'Fill from map'}
                </button>
                {geocodeLabel && (
                  <span className="text-gray-500 truncate">{geocodeLabel}</span>
                )}
              </div>

              {(editData.latitude && editData.longitude) && (
                <div className="mt-2">
                  <MapComponent
                    height="240px"
                    center={[parseFloat(editData.latitude), parseFloat(editData.longitude)]}
                    zoom={15}
                    markers={[{
                      id: property.id,
                      lat: parseFloat(editData.latitude),
                      lng: parseFloat(editData.longitude),
                      title: editData.title || property.title,
                      color: '#EF4444'
                    }]}
                    draggableMarkerId={property.id}
                    onMarkerDrag={(marker) => {
                      handleEditChange('latitude', marker.lat);
                      handleEditChange('longitude', marker.lng);
                    }}
                    showControls={true}
                  />
                </div>
              )}

              <div className="flex space-x-2">
                <button
                  onClick={handleSave}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  Save
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
            ) : (
            <div>
              <h4 className="font-medium text-gray-900">{property.title}</h4>
              <p className="text-sm text-gray-600">{property.description}</p>
              <p className="text-sm text-gray-500">{property.address}, {property.city}</p>
              <p className="text-sm text-gray-500">{formatPrice(property.price, property.currency)} • {property.property_type}</p>
              <p className="text-sm text-gray-500">Reported by: {property.user?.name}</p>
              
              {/* Location Information */}
              {(property.gps_latitude || property.manual_latitude) && (
                <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                  <div className="font-semibold text-gray-700 mb-1">📍 Locations:</div>
                  <div className="space-y-1">
                    {property.gps_latitude && (
                      <div className="text-gray-600">
                        GPS: {parseFloat(property.gps_latitude).toFixed(6)}, {parseFloat(property.gps_longitude).toFixed(6)}
                      </div>
                    )}
                    {property.manual_latitude && (
                      <div className="text-gray-600">
                        User: {parseFloat(property.manual_latitude).toFixed(6)}, {parseFloat(property.manual_longitude).toFixed(6)}
                      </div>
                    )}
                    <div className="text-gray-500 italic">
                      Primary: {parseFloat(property.latitude).toFixed(6)}, {parseFloat(property.longitude).toFixed(6)} ({property.location_source === 'manual' ? 'User' : 'GPS'})
                    </div>
                  </div>
                </div>
              )}
              
              {(() => {
                const isReportedSold = property.status === 'pending' && property.rejection_reason === 'reported_sold';
                const label = isReportedSold ? 'reported_sold' : property.status;
                const badgeClass =
                  label === 'approved' ? 'bg-green-100 text-green-800' :
                  label === 'sold' ? 'bg-red-600 text-white' :
                  label === 'rejected' ? 'bg-red-100 text-red-800' :
                  isReportedSold ? 'bg-orange-100 text-orange-800' :
                  'bg-yellow-100 text-yellow-800';
                return (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${badgeClass}`}>
                      {label}
                    </span>
                    {property.is_revised && (
                      <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700">
                        Revised
                      </span>
                    )}
                    {property.seller_marked_sold && (
                      <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                        Seller marked sold
                      </span>
                    )}
                    {property.allow_agent_contact && (
                      <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800" title={t('admin.allowAgentContactBadge')}>
                        {t('admin.allowAgentContactBadge')}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
        
        <div className="flex flex-col space-y-2">
          <button
            onClick={() => (showEdit ? handleCancelEdit() : handleStartEdit())}
            className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
          >
            {showEdit ? 'Close editor' : 'Edit'}
          </button>

          {onCreateVerificationLink && (
            <button
              onClick={() => onCreateVerificationLink(property.id)}
              disabled={isCreatingVerificationLink}
              className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-60"
            >
              {isCreatingVerificationLink ? 'Generating…' : 'Create verification link'}
            </button>
          )}

          {verificationLink && (
            <div className="rounded border border-indigo-200 bg-indigo-50 p-2 text-xs text-indigo-900 space-y-2">
              <div className="break-all">{verificationLink}</div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs text-indigo-800">
                  {t('admin.verification.languageLabel')}
                </label>
                <select
                  value={whatsappLanguage}
                  onChange={(event) => setWhatsappLanguage(event.target.value)}
                  className="rounded border border-indigo-300 bg-white px-2 py-1 text-xs text-indigo-800"
                >
                  <option value="en">{t('language.english')}</option>
                  <option value="es">{t('language.spanish')}</option>
                  <option value="de">{t('language.german')}</option>
                  <option value="ru">{t('language.russian')}</option>
                  <option value="zh">{t('language.chinese')}</option>
                  <option value="ar">{t('language.arabic')}</option>
                  <option value="fr">{t('language.french')}</option>
                </select>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(verificationLink);
                      toast.success('Verification link copied');
                    } catch (err) {
                      toast.error('Failed to copy link');
                    }
                  }}
                  className="px-2 py-1 text-xs rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                >
                  Copy
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const fixedT = i18n.getFixedT(whatsappLanguage);
                    const message = fixedT('admin.verification.whatsappTemplate', { link: verificationLink });
                    try {
                      await navigator.clipboard.writeText(message);
                      toast.success('WhatsApp message copied');
                    } catch (err) {
                      toast.error('Failed to copy message');
                    }
                  }}
                  className="px-2 py-1 text-xs rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                >
                  Copy WhatsApp text
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const rawNumber = property.whatsapp || property.phone || '';
                    const normalized = String(rawNumber).replace(/[^0-9]/g, '');
                    if (!normalized) {
                      toast.error('No WhatsApp or phone number available');
                      return;
                    }
                    const fixedT = i18n.getFixedT(whatsappLanguage);
                    const message = fixedT('admin.verification.whatsappTemplate', { link: verificationLink });
                    const url = `https://wa.me/${normalized}?text=${encodeURIComponent(message)}`;
                    window.open(url, '_blank');
                  }}
                  disabled={!property.whatsapp && !property.phone}
                  className="px-2 py-1 text-xs rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                >
                  Open WhatsApp
                </button>
              </div>
            </div>
          )}
          
          {!showEdit && (
            <button
              onClick={handleDelete}
              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
            >
              Delete
            </button>
          )}

          {!showEdit && (
            <>
              {property.status !== 'approved' && (
                <button
                  onClick={handleApprove}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  Approve
                </button>
              )}
              {property.status !== 'rejected' && (
                <button
                  onClick={() => {
                    const confirmed = window.confirm('Block this listing?');
                    if (!confirmed) return;
                    onReject(property.id, 'blocked_by_admin');
                  }}
                  className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                >
                  Block
                </button>
              )}
            </>
          )}
          
          {property.status === 'pending' && !showEdit && (
            <>
              <button
                onClick={() => setShowActions(!showActions)}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                Manage
              </button>
              {showActions && (
                <div className="space-y-2">
                  {/* Commission controls removed */}
                  <button
                    onClick={handleApprove}
                    className="w-full px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <div className="space-y-1">
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Rejection reason..."
                      className="w-full px-2 py-1 text-sm border rounded"
                      rows="2"
                    />
                    <button
                      onClick={handleReject}
                      className="w-full px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Image Gallery */}
      {showImages && (
        <div className="mt-4 border-t pt-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <h5 className="text-sm font-medium text-gray-900">
              Property Images ({property.images?.length || 0})
            </h5>
            <div className="flex items-center gap-2">
              <input
                ref={uploadInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={handleUploadMediaChange}
              />
              <button
                type="button"
                onClick={handleUploadMediaClick}
                disabled={isUploading}
                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
              >
                {isUploading ? 'Uploading…' : 'Upload media'}
              </button>
            </div>
          </div>
          {(isUploading || isDragActive) && (
            <div className="mb-3">
              {isUploading ? (
                <div className="w-full">
                  <div className="text-xs text-gray-600 mb-1">Uploading… {uploadProgress}%</div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
                  Drop files to upload
                </div>
              )}
            </div>
          )}
          {property.images && property.images.length > 0 ? (
            <div
              className={`grid grid-cols-2 md:grid-cols-4 gap-4 border-2 border-dashed rounded-lg p-3 transition-colors ${
                isDragActive ? 'border-blue-400 bg-blue-50' : 'border-transparent'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {property.images.map((image, index) => {
                const imageSrc = getImageSrc ? getImageSrc(image) : null;
                const isVideo = image.file_type === 'video';
                return (
                  <div key={index} className="relative group border rounded-lg overflow-hidden">
                    {imageSrc ? (
                      isVideo ? (
                        <video
                          src={imageSrc}
                          className="w-full h-24 object-cover"
                          controls
                        />
                      ) : (
                        <img
                          src={imageSrc}
                          alt={`Property ${index + 1}`}
                          className="w-full h-24 object-cover cursor-pointer hover:opacity-75"
                          onClick={() => window.open(imageSrc, '_blank')}
                        />
                      )
                    ) : (
                      <div className="w-full h-24 bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
                        No Media
                      </div>
                    )}
                    {image.is_primary && (
                      <span className="absolute top-1 left-1 bg-blue-600 text-white text-xs px-1 rounded">
                        Primary
                      </span>
                    )}
                    {imageSrc && (
                      <a
                        href={imageSrc}
                        download
                        target="_blank"
                        rel="noreferrer"
                        className="absolute top-1 right-12 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Download
                      </a>
                    )}
                    <button
                      onClick={() => handleDeleteMedia(image)}
                      className="absolute top-1 right-1 px-2 py-1 text-xs bg-red-600 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      Delete
                    </button>
                    {isVideo && (
                      <span className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-[10px] px-1 rounded">
                        Video
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              className={`text-xs text-gray-500 bg-gray-50 rounded p-3 border-2 border-dashed transition-colors ${
                isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              No media uploaded yet. Drag & drop files here or use Upload media.
            </div>
          )}
        </div>
      )}

      {/* Contact Information */}
      {!showEdit && (property.phone || property.whatsapp || property.email) && (
        <div className="mt-4 border-t pt-4">
          <h5 className="text-sm font-medium text-gray-900 mb-2">Contact Information</h5>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
            {property.phone && <div>📞 Phone: {property.phone}</div>}
            {property.whatsapp && <div>💬 WhatsApp: {property.whatsapp}</div>}
            {property.email && <div>📧 Email: {property.email}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

// Commission Card Component
// eslint-disable-next-line no-unused-vars
const CommissionCard = ({ commission }) => {
  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-gray-900">{commission.property?.title}</h4>
          <p className="text-sm text-gray-600">User: {commission.user?.name}</p>
          <p className="text-sm text-gray-500">Amount: ${commission.amount} ({commission.commission_rate}%)</p>
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          commission.status === 'paid' ? 'bg-green-100 text-green-800' :
          commission.status === 'approved' ? 'bg-blue-100 text-blue-800' :
          commission.status === 'rejected' ? 'bg-red-100 text-red-800' :
          'bg-yellow-100 text-yellow-800'
        }`}>
          {commission.status}
        </span>
      </div>
    </div>
  );
};

const SubscriptionBuyersPanel = ({ transactions, buyers, loading, month, year }) => {
  const monthLabel = new Date(year, month - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6 space-y-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Subscription customers</h3>
          <p className="text-sm text-gray-500">Completed subscription purchases for {monthLabel}.</p>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500">Loading subscription data…</div>
        ) : buyers.length === 0 ? (
          <div className="text-sm text-gray-500">No subscription purchases recorded for this month.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2">Subscriber</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Purchases</th>
                  <th className="text-left px-3 py-2">Total paid</th>
                  <th className="text-left px-3 py-2">Reporter share</th>
                  <th className="text-left px-3 py-2">Platform share</th>
                  <th className="text-left px-3 py-2">Last purchase</th>
                </tr>
              </thead>
              <tbody>
                {buyers.map((buyer) => (
                  <tr key={buyer.id} className="border-t">
                    <td className="px-3 py-2 font-medium text-gray-900">{buyer.name}</td>
                    <td className="px-3 py-2 text-gray-600">{buyer.email}</td>
                    <td className="px-3 py-2">{buyer.transactions}</td>
                    <td className="px-3 py-2">${money(buyer.totalAmount)}</td>
                    <td className="px-3 py-2 text-green-600">${money(buyer.reporterShare)}</td>
                    <td className="px-3 py-2 text-indigo-600">${money(buyer.platformShare)}</td>
                    <td className="px-3 py-2">{buyer.lastPurchase ? buyer.lastPurchase.toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {transactions.length > 0 && (
          <div className="text-xs text-gray-500">
            Showing {buyers.length} subscriber{buyers.length === 1 ? '' : 's'} from {transactions.length} completed transaction{transactions.length === 1 ? '' : 's'}.
          </div>
        )}
      </div>
    </div>
  );
};

// Earnings Card Component
const EarningsCard = ({ user }) => {
  const [showPayout, setShowPayout] = useState(false);
  const [showCancelPayout, setShowCancelPayout] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');

  const handleProcessPayout = async () => {
    if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
      alert('Please enter a valid payout amount');
      return;
    }

    if (parseFloat(payoutAmount) > user.pending_payout) {
      alert('Payout amount cannot exceed pending payout');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/earnings/${user.id}/payout`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          amount: parseFloat(payoutAmount),
          notes: payoutNotes
        }),
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Payout processed successfully!');
        setShowPayout(false);
        setPayoutAmount('');
        setPayoutNotes('');
        // Reload earnings data
        window.location.reload();
      } else {
        toast.error(data.message || 'Failed to process payout');
      }
    } catch (error) {
      console.error('Error processing payout:', error);
      toast.error('Failed to process payout');
    }
  };

  const handleCancelPayout = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to cancel the pending payout of $${user.pending_payout} for ${user.name}?\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(apiUrl(`/admin/earnings/${user.id}/cancel-payout`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          notes: cancelNotes || 'Pending payout cancelled by admin'
        }),
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`Payout cancelled successfully! Cancelled amount: $${data.data.cancelled_amount}`);
        setShowCancelPayout(false);
        setCancelNotes('');
        // Reload earnings data
        window.location.reload();
      } else {
        toast.error(data.message || 'Failed to cancel payout');
      }
    } catch (error) {
      console.error('Error cancelling payout:', error);
      toast.error('Failed to cancel payout');
    }
  };

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h4 className="font-medium text-gray-900">{user.name || 'Unknown User'}</h4>
          <p className="text-sm text-gray-600">{user.email}</p>
          <div className="flex space-x-4 mt-2">
            <div className="text-sm">
              <span className="text-gray-500">Total Earnings:</span>
              <span className="font-medium text-green-600 ml-1">${user.earnings || 0}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Pending Payout:</span>
              <span className="font-medium text-orange-600 ml-1">${user.pending_payout || 0}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col space-y-2">
          {user.pending_payout >= 50 && (
            <>
              <button
                onClick={() => setShowPayout(!showPayout)}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                Process Payout
              </button>
              <button
                onClick={() => setShowCancelPayout(!showCancelPayout)}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
              >
                Cancel Payout
              </button>
            </>
          )}
          {user.pending_payout > 0 && user.pending_payout < 50 && (
            <div className="text-xs text-gray-500">Minimum payout is $50. Pending: ${user.pending_payout}</div>
          )}
          {showPayout && (
            <div className="space-y-2 bg-gray-50 p-3 rounded border">
              <div>
                <label className="text-xs text-gray-600">Amount:</label>
                <input
                  type="number"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="0.00"
                  max={user.pending_payout}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Notes:</label>
                <textarea
                  value={payoutNotes}
                  onChange={(e) => setPayoutNotes(e.target.value)}
                  className="w-full px-2 py-1 text-sm border rounded"
                  rows="2"
                  placeholder="Optional notes..."
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleProcessPayout}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  Process
                </button>
                <button
                  onClick={() => setShowPayout(false)}
                  className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {showCancelPayout && (
            <div className="space-y-2 bg-red-50 p-3 rounded border border-red-200">
              <div className="text-sm text-red-800 font-medium">
                Cancel Pending Payout: ${user.pending_payout}
              </div>
              <div>
                <label className="text-xs text-gray-600">Cancellation Notes:</label>
                <textarea
                  value={cancelNotes}
                  onChange={(e) => setCancelNotes(e.target.value)}
                  className="w-full px-2 py-1 text-sm border rounded"
                  rows="2"
                  placeholder="Reason for cancellation..."
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleCancelPayout}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                >
                  Confirm Cancel
                </button>
                <button
                  onClick={() => setShowCancelPayout(false)}
                  className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
